import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ShoppingBag, 
  Layers, 
  Settings, 
  Terminal, 
  CheckCircle, 
  HelpCircle, 
  AlertTriangle, 
  Cpu, 
  FileText, 
  ArrowRight, 
  Bookmark, 
  Plus, 
  Trash2, 
  Edit3, 
  Download, 
  Zap, 
  Coins, 
  ExternalLink,
  Laptop,
  Database,
  Gauge,
  PhoneCall,
  Save,
  Grid,
  TrendingUp,
  Inbox,
  Clock,
  Shield,
  Eye,
  Check,
  Search,
  SlidersHorizontal,
  ChevronDown,
  X,
  CreditCard,
  Receipt,
  CheckSquare,
  Wand2
} from 'lucide-react';
import { DigitalProduct, INITIAL_DIGITAL_PRODUCTS } from '../data/nexusDigitalProducts';
import { NexusUser } from '../types/nexus';
import { NexusDigitalProductFactory } from './NexusDigitalProductFactory';
import { NexusDigitalAssetLibrary } from './NexusDigitalAssetLibrary';

interface NexusDigitalProductMarketplaceProps {
  user: NexusUser;
  isAdmin?: boolean;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onAddTaskToWorkSuite?: (title: string, priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW') => void;
}

export const NexusDigitalProductMarketplace: React.FC<NexusDigitalProductMarketplaceProps> = ({
  user,
  isAdmin = false,
  onAddLog,
  onAddTaskToWorkSuite
}) => {
  // --- Persistent State ---
  const [products, setProducts] = useState<DigitalProduct[]>(() => {
    const saved = localStorage.getItem('nexorax_saved_digital_products');
    if (saved) {
      const parsed = JSON.parse(saved);
      const merged = [...parsed];
      INITIAL_DIGITAL_PRODUCTS.forEach(initial => {
        if (!merged.some(p => p.id === initial.id)) {
          merged.push(initial);
        }
      });
      return merged;
    }
    return INITIAL_DIGITAL_PRODUCTS;
  });

  const [publicInquiries, setPublicInquiries] = useState<any[]>(() => {
    const saved = localStorage.getItem('nexorax_public_inquiries');
    return saved ? JSON.parse(saved) : [];
  });

  // Unified Request Wizard Options
  const [activeRequestProduct, setActiveRequestProduct] = useState<DigitalProduct | null>(null);
  const [requestStep, setRequestStep] = useState(1);
  const [requestConfig, setRequestConfig] = useState({
    deliveryOption: 'Managed Launch Bundle' as 'template' | 'dfy' | 'Managed Launch Bundle',
    extras: {
      hosting: true,
      domain: true,
      email: true,
      support: true,
      chat: false,
      seo: false,
      social: false
    },
    clientName: user?.displayName || '',
    clientBusiness: '',
    clientEmail: user?.email || '',
    message: ''
  });

  const [activeTab, setActiveTab] = useState<'Ready-Made Website' | 'Ready-Made Mobile App' | 'AI Assistant Template' | 'Automation Workflow' | 'Make.com Workflow' | 'n8n Workflow' | 'Code Pack' | 'Dashboard Template' | 'Social Media Pack' | 'Nexus Bundle'>('Ready-Made Website');
  const [activeLibraryTab, setActiveLibraryTab] = useState<string>('Digital Asset Library');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState<'ALL' | 'Beginner' | 'Intermediate' | 'Advanced' | 'Done-For-You Recommended'>('ALL');
  const [selectedLicence, setSelectedLicence] = useState<string>('ALL');
  const [sortBy, setSortBy] = useState<'name' | 'latest' | 'price_low' | 'price_high' | 'priority'>('priority');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Modal / Interactive Flow States
  const [selectedProduct, setSelectedProduct] = useState<DigitalProduct | null>(null);
  const [activeWorkflowProduct, setActiveWorkflowProduct] = useState<DigitalProduct | null>(null);
  const [activeCodeProduct, setActiveCodeProduct] = useState<DigitalProduct | null>(null);
  const [watchlist, setWatchlist] = useState<string[]>(() => {
    const saved = localStorage.getItem('nexorax_digital_watchlist');
    return saved ? JSON.parse(saved) : [];
  });

  // Client Dashboard Delivery Tracking
  const [clientDeliveries, setClientDeliveries] = useState<any[]>(() => {
    const saved = localStorage.getItem('nexorax_client_deliveries');
    return saved ? JSON.parse(saved) : [];
  });

  // Flow Wizards
  const [websiteBuilderProduct, setWebsiteBuilderProduct] = useState<DigitalProduct | null>(null);
  const [websiteBuilderStep, setWebsiteBuilderStep] = useState(1);
  const [websiteConfig, setWebsiteConfig] = useState({
    subCategory: '',
    deliveryMethod: 'Nexus Hosting Setup' as any,
    addHosting: true,
    addDomain: false,
    addMail: false,
    addSEO: false,
    builderType: 'done-for-you' as 'done-for-you' | 'template'
  });

  const [appBuilderProduct, setAppBuilderProduct] = useState<DigitalProduct | null>(null);
  const [appBuilderStep, setAppBuilderStep] = useState(1);
  const [appConfig, setAppConfig] = useState({
    subCategory: '',
    techStack: 'React Native / Flutter Combo',
    hostingBackend: 'Node Server + Firestore Container',
    includeAdminPanel: true,
    setupSupport: 'Done-For-You Implementation'
  });

  // Admin Tooling States
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminIsEditing, setAdminIsEditing] = useState<string | null>(null);
  const [adminForm, setAdminForm] = useState<Partial<DigitalProduct>>({
    productName: '',
    productType: 'Ready-Made Website',
    category: 'Business Website',
    shortDescription: '',
    fullDescription: '',
    previewImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800',
    demoUrl: '',
    productScreenshots: [],
    includedFiles: [],
    techStack: [],
    integrations: [],
    setupDifficulty: 'Beginner',
    deliveryMethod: 'Digital Download',
    licenceType: 'Business Use',
    setupPrice: 399,
    productPrice: 99,
    monthlySupportPrice: 29,
    paymentStatus: 'Request Invoice',
    checkoutStatus: 'Invoice Only',
    productStatus: 'Draft',
    buildStatus: 'Template Ready',
    approvalStatus: 'Admin Draft',
    tags: [],
    featured: false,
    internalCost: 45,
    marginEstimate: 54,
    buildNotes: '',
    riskNotes: '',
    providerNotes: '',
    supplierNotes: '',
    fileStorageNotes: ''
  });

  // Sync to database/localStorage
  useEffect(() => {
    localStorage.setItem('nexorax_saved_digital_products', JSON.stringify(products));
  }, [products]);

  useEffect(() => {
    localStorage.setItem('nexorax_public_inquiries', JSON.stringify(publicInquiries));
  }, [publicInquiries]);

  useEffect(() => {
    localStorage.setItem('nexorax_digital_watchlist', JSON.stringify(watchlist));
  }, [watchlist]);

  useEffect(() => {
    localStorage.setItem('nexorax_client_deliveries', JSON.stringify(clientDeliveries));
  }, [clientDeliveries]);

  // Handle direct request logs
  const handleRequestSetup = (product: DigitalProduct, optionsDetails?: string) => {
    onAddLog(
      'Nexus Marketplace',
      `Setup Requested: "${product.productName}"`,
      'PENDING_APPROVAL',
      `B2B delivery package queued. Total template Price: £${product.productPrice}. Setup Fee: £${product.setupPrice}. Support Plan: £${product.monthlySupportPrice}/mo. Details: ${optionsDetails || 'Default configurations'}`
    );

    // Create client delivery node
    const newDelivery = {
      id: `del-${Date.now()}`,
      productId: product.id,
      productName: product.productName,
      previewImage: product.previewImage,
      type: product.productType,
      config: optionsDetails || 'Done-For-You Setup (Custom)',
      status: 'Request Received',
      checklist: [
        { task: 'Register Delivery Instance', done: true },
        { task: 'B2B Invoice Despatch & Payment Verification', done: false },
        { task: 'Audit Server Capacity & Ingress routing', done: false },
        { task: 'Provision Sandbox Environment', done: false },
        { task: 'Install Project Starter Assets', done: false },
        { task: 'Live Validation & Developer Release', done: false },
        { task: 'MFA Credential Handover via Secure Voice Channels', done: false }
      ],
      files: product.includedFiles,
      demoLink: product.demoUrl || '',
      invoiceStatus: 'Pending Invoice Draft',
      supportStatus: 'Awaiting Activation',
      workSuiteId: `ws-del-${Date.now().toString().slice(-4)}`
    };

    setClientDeliveries(prev => [newDelivery, ...prev]);

    // Push task into WorkSuite
    if (onAddTaskToWorkSuite) {
      onAddTaskToWorkSuite(`DEPLOY: ${product.productName} for ${user.email}`, 'HIGH');
    }

    alert(
      `SUCCESS! YOUR APPLICATION IS QUEUED.\n\n` +
      `Product: ${product.productName}\n` +
      `B2B request has been dispatched to NexoraX Staff Coordinator.\n` +
      `Check your "B2B Delivery Checklist" below to track active setup sprints!`
    );
  };

  const handleWatchlistToggle = (productId: string) => {
    if (watchlist.includes(productId)) {
      setWatchlist(prev => prev.filter(id => id !== productId));
      onAddLog('Nexus Marketplace', `Watchlist Removed: "${productId}"`, 'SUCCESS', 'Removed target item package tracker.');
    } else {
      setWatchlist(prev => [...prev, productId]);
      onAddLog('Nexus Marketplace', `Watchlist Added: "${productId}"`, 'SUCCESS', 'Bookmarked catalog digital product code assets.');
    }
  };

  // --- Search and Filters ---
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesType = p.productType === activeTab;
      const matchesSearch = p.productName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            p.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            p.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            p.tags.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesDifficulty = selectedDifficulty === 'ALL' || p.setupDifficulty === selectedDifficulty;
      const matchesLicence = selectedLicence === 'ALL' || p.licenceType === selectedLicence;
      
      // Public view sees published or coming soon items only
      const matchesPublicStatus = isAdmin || p.productStatus !== 'Draft';

      return matchesType && matchesSearch && matchesDifficulty && matchesLicence && matchesPublicStatus;
    });
  }, [products, activeTab, searchTerm, selectedDifficulty, selectedLicence, isAdmin]);

  // Sort logic
  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    if (sortBy === 'name') {
      return list.sort((a, b) => a.productName.localeCompare(b.productName));
    } else if (sortBy === 'latest') {
      return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (sortBy === 'price_low') {
      return list.sort((a, b) => a.productPrice - b.productPrice);
    } else if (sortBy === 'price_high') {
      return list.sort((a, b) => b.productPrice - a.productPrice);
    } else if (sortBy === 'priority') {
      return list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }
    return list;
  }, [filteredProducts, sortBy]);

  // --- Admin Form Utilities ---
  const handleAdminFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminForm.productName || !adminForm.productType) {
      alert('Product Name and Product Type are required fields.');
      return;
    }

    if (adminIsEditing) {
      setProducts(prev => prev.map(p => p.id === adminIsEditing ? {
        ...p,
        ...adminForm,
        id: adminIsEditing,
        updatedAt: new Date().toISOString()
      } as DigitalProduct : p));
      onAddLog('Admin Marketplace', `Edited Digital Product: "${adminForm.productName}"`, 'SUCCESS', 'Completed metadata specifications database update.');
      setAdminIsEditing(null);
    } else {
      const newProduct: DigitalProduct = {
        ...(adminForm as DigitalProduct),
        id: `ndp-custom-${Date.now()}`,
        productScreenshots: adminForm.productScreenshots || [],
        includedFiles: adminForm.includedFiles || ['src/App.tsx'],
        techStack: adminForm.techStack || ['React'],
        integrations: adminForm.integrations || [],
        tags: adminForm.tags || ['Custom'],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setProducts(prev => [...prev, newProduct]);
      onAddLog('Admin Marketplace', `Created Digital Product: "${newProduct.productName}"`, 'SUCCESS', 'Dispatched new custom asset node registry schema successfully.');
    }

    resetAdminForm();
  };

  const resetAdminForm = () => {
    setAdminForm({
      productName: '',
      productType: 'Ready-Made Website',
      category: 'Business Website',
      shortDescription: '',
      fullDescription: '',
      previewImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800',
      demoUrl: '',
      productScreenshots: [],
      includedFiles: [],
      techStack: [],
      integrations: [],
      setupDifficulty: 'Beginner',
      deliveryMethod: 'Digital Download',
      licenceType: 'Business Use',
      setupPrice: 399,
      productPrice: 99,
      monthlySupportPrice: 29,
      paymentStatus: 'Request Invoice',
      checkoutStatus: 'Invoice Only',
      productStatus: 'Draft',
      buildStatus: 'Template Ready',
      approvalStatus: 'Admin Draft',
      tags: [],
      featured: false,
      internalCost: 45,
      marginEstimate: 54,
      buildNotes: '',
      riskNotes: '',
      providerNotes: '',
      supplierNotes: '',
      fileStorageNotes: ''
    });
    setAdminIsEditing(null);
  };

  const handleEditProduct = (prod: DigitalProduct) => {
    setAdminForm(prod);
    setAdminIsEditing(prod.id);
    setShowAdminPanel(true);
  };

  const handleDeleteProduct = (id: string, name: string) => {
    if (window.confirm(`DANGER: Are you sure you want to delete Digital Product "${name}"?`)) {
      setProducts(prev => prev.filter(p => p.id !== id));
      onAddLog('Admin Marketplace', `Deleted Digital Product: "${name}"`, 'SUCCESS', `Discarded database node ${id}.`);
    }
  };

  return (
    <div className="w-full space-y-8 bg-[#020205] text-slate-100 min-h-screen pb-16 relative">
      
      {/* 1. Header Hero Panel */}
      <div className="relative overflow-hidden rounded-2xl border border-purple-950/40 bg-black/60 p-6 lg:p-8 shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-950/20 via-black/40 to-[#00ffff]/5 pointer-events-none z-0" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#e840f3]/10 rounded-full blur-3xl pointer-events-none -z-10" />
        
        <div className="relative z-10 flex flex-col lg:flex-row justify-between gap-6 items-start lg:items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-[#e840f3]/10 text-[#e840f3] py-1 px-3 rounded-full border border-[#e840f3]/35 text-[9px] font-mono uppercase tracking-widest font-black mb-3">
              <Zap className="w-3 h-3 animate-pulse" />
              NEXUS DIGIPLACE
            </div>
            <h1 className="text-2xl lg:text-3.5xl font-sans font-black tracking-tight text-white uppercase leading-none">
              Nexus Digital Product Marketplace
            </h1>
            <p className="text-glow-cyan text-xs font-mono text-cyan-400 mt-1 uppercase tracking-widest leading-none">
              Ready-Made Websites, Apps &amp; AI Workflows
            </p>
            <p className="text-zinc-400 text-xs mt-3 max-w-3xl leading-relaxed normal-case">
              Explore premium ready-made websites, mobile app templates, operational AI assistants, scalable dashboards, custom integrations, and reusable code files. Configure setup parameters, request manual invoices, or add items directly to your Client Delivery WorkSuite.
            </p>
          </div>
          
          {/* Admin Mode floating toggle */}
          {isAdmin && (
            <div className="bg-purple-950/30 border border-purple-900/50 rounded-xl p-3 flex flex-col gap-2 shrink-0 sm:w-64 font-mono">
              <div className="flex justify-between items-center text-[10px] text-purple-300 font-bold tracking-wider uppercase">
                <span>Director Console</span>
                <span className="w-2 h-2 rounded-full bg-[#e840f3] animate-ping" />
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowAdminPanel(!showAdminPanel);
                  resetAdminForm();
                }}
                className="w-full py-2 bg-gradient-to-r from-purple-800 to-indigo-700 text-white font-mono text-[9px] font-black tracking-widest rounded-lg uppercase hover:brightness-110 active:scale-[0.98] transition-all cursor-pointer flex items-center justify-center gap-1.5 border border-purple-500/30"
              >
                <Settings className="w-3.5 h-3.5" />
                {showAdminPanel ? 'Exit Product Editor' : 'Open Product Editor'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* --- MASTER NAVIGATION MATRIX (10 TABS REQUIRED) --- */}
      <div className="border-b border-zinc-900 pb-3 z-10 relative">
        <div className="flex flex-wrap gap-2 text-[9px] font-mono font-black uppercase tracking-widest justify-start items-center">
          {[
            'Digital Product Store',
            'Digital Asset Library',
            'Website Templates',
            'App Templates',
            'Dashboard Templates',
            'UI Kits',
            'Slide Decks',
            'Code Packs',
            'Workflow Templates',
            'Brand & Social Packs'
          ].map((tab) => {
            const isActive = activeLibraryTab === tab;
            return (
              <button
                key={tab}
                id={`master-tab-${tab.toLowerCase().replace(/[^a-z50-9]/g, '-')}`}
                onClick={() => {
                  setActiveLibraryTab(tab);
                  onAddLog('Storefront', `Navigated to catalog division: ${tab}`, 'SUCCESS', `Render mode updated.`);
                }}
                className={`py-2 px-3.5 rounded-xl cursor-pointer transition-all border ${
                  isActive
                    ? 'bg-purple-950/40 text-[#e840f3] border-[#e840f3]/45 shadow-[0_0_12px_rgba(232,64,243,0.15)] font-black'
                    : 'text-zinc-500 hover:text-zinc-300 bg-[#06060c]/40 border-zinc-900'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>
      </div>

      {activeLibraryTab !== 'Digital Product Store' ? (
        <NexusDigitalAssetLibrary
          user={user}
          products={products}
          setProducts={setProducts}
          activeLibraryTab={activeLibraryTab}
          setActiveLibraryTab={setActiveLibraryTab}
          onAddLog={onAddLog}
          onAddTaskToWorkSuite={onAddTaskToWorkSuite}
        />
      ) : (
        <>
          {/* 2. Disclaimers Compliance Block */}
      <div className="bg-[#0b0c1b]/85 border border-yellow-500/20 rounded-xl p-4.5 text-[11px] text-zinc-400 leading-normal flex gap-3 items-start shadow-inner font-sans">
        <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
        <div className="space-y-1.5">
          <p className="text-yellow-400 font-bold uppercase tracking-wider text-[9px] font-mono">NexoraX Public Safety &amp; Accuracy Disclosures: REQUIRED BY LEAD DISTRICT COMPLIANCE</p>
          <p>
            <strong className="text-amber-200">Digital Product Disclaimer:</strong> Ready-made templates, workflows, and code-packs require manual configuration, active provider subscription licenses (Make.com, n8n, OpenAI, Google Clouds), custom domain name mappings, and rigorous testing environments before production release.
          </p>
          <p>
            <strong className="text-[#00ffff] uppercase tracking-wider font-mono">Ready-Made Digital Products Terms:</strong> Ready-made websites, mobile apps, dashboards, PDFs, pitch decks, social media packs, and automation packs are pay-before-download products. No custom setup, hosting, domain, email, SEO, copywriting, or deployment is included unless stated or quoted separately.
          </p>
          <p>
            <strong className="text-cyan-400">Security Warning:</strong> Code files are supplied as foundational templates. Proper security audits, credential separation, API authorization checks, and pen-testing validations are highly recommended before binding databases or deploying on public ports. Zero API keys are hard-coded.
          </p>
          <p>
            <strong className="text-purple-400">Human Approval Rule:</strong> Operational AI and automations (draft replies, lead emails, billing invoices) draft parameters for review only. Human operator approval is strictly required before issuing outbound correspondence or active fiscal transactions.
          </p>
        </div>
      </div>

      {/* 3. Product Sub-Tabs Switching */}
      <div className="border-b border-zinc-900 pb-2.5">
        <div className="flex flex-wrap gap-2 text-[9px] font-mono font-black uppercase tracking-widest">
          {[
            { tag: 'Ready-Made Website', label: 'Websites' },
            { tag: 'Ready-Made Mobile App', label: 'Mobile Apps' },
            { tag: 'AI Assistant Template', label: 'AI Assistants' },
            { tag: 'Automation Workflow', label: 'Workflows' },
            { tag: 'Make.com Workflow', label: 'Make.com Blueprints' },
            { tag: 'n8n Workflow', label: 'n8n Flowcharts' },
            { tag: 'Code Pack', label: 'Code Packs' },
            { tag: 'Dashboard Template', label: 'Dashboards' },
            { tag: 'Social Media Pack', label: 'Social Media' },
            { tag: 'Nexus Bundle', label: 'Nexus Bundles' }
          ].map((tab) => (
            <button
              key={tab.tag}
              onClick={() => setActiveTab(tab.tag as any)}
              className={`py-2 px-3.5 rounded-xl cursor-pointer transition-all border ${
                activeTab === tab.tag
                  ? 'bg-purple-950/50 text-[#e840f3] border-[#e840f3]/45 shadow-[0_0_12px_rgba(232,64,243,0.15)] font-black'
                  : 'text-zinc-400 hover:text-white bg-[#06060c]/40 border-zinc-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 4. Controls, Filters and Search Bar */}
      <div className="bg-[#04050e]/60 border border-zinc-900/60 rounded-xl p-4 flex flex-col md:flex-row gap-4 justify-between items-center">
        
        {/* Search */}
        <div className="relative w-full md:max-w-md">
          <Search className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            placeholder="Search ready-made digital products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-black/40 border border-zinc-850 rounded-xl text-xs font-mono text-white placeholder-zinc-500 focus:outline-none focus:border-[#e840f3]/50 focus:shadow-[0_0_10px_rgba(232,64,243,0.1)]"
          />
        </div>

        {/* Filter buttons & Sorting dropdown */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
          <button
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            className={`py-2.5 px-4 bg-zinc-950 rounded-xl text-xs font-mono font-bold flex items-center gap-2 border cursor-pointer border-zinc-850 hover:text-[#00ffff] transition-colors ${isFiltersOpen ? 'text-[#00ffff] border-[#00ffff]/40 bg-[#00ffff]/5' : 'text-slate-400'}`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span>Filter Parameters</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${isFiltersOpen ? 'rotate-180' : ''}`} />
          </button>

          <div className="flex items-center gap-2 shrink-0 bg-black/30 border border-zinc-900 rounded-xl px-3 py-1 bg-zinc-950">
            <span className="text-[10px] font-mono text-zinc-500 uppercase font-black">Sort:</span>
            <select
              value={sortBy}
              onChange={(e: any) => setSortBy(e.target.value)}
              className="bg-transparent text-xs text-slate-300 font-mono focus:outline-none cursor-pointer"
            >
              <option value="priority">Priority Order</option>
              <option value="name">Product Name (A-Z)</option>
              <option value="latest">Latest Released</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mobile-collapsible detail filters */}
      <AnimatePresence>
        {isFiltersOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-[#03040c] border border-zinc-900 rounded-xl p-4 font-mono text-xs text-slate-300"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              
              {/* Difficulty filter */}
              <div className="space-y-2">
                <span className="text-[10px] text-cyan-400 uppercase font-black tracking-wider block">Complexity Scale:</span>
                <div className="flex flex-wrap gap-1.5">
                  {['ALL', 'Beginner', 'Intermediate', 'Advanced', 'Done-For-You Recommended'].map((dif) => (
                    <button
                      key={dif}
                      onClick={() => setSelectedDifficulty(dif as any)}
                      className={`py-1 px-2.5 rounded text-[10px] border cursor-pointer ${selectedDifficulty === dif ? 'bg-cyan-500/10 text-cyan-300 border-cyan-500/45' : 'bg-black/30 border-zinc-900 text-zinc-400 hover:text-slate-200'}`}
                    >
                      {dif}
                    </button>
                  ))}
                </div>
              </div>

              {/* License list */}
              <div className="space-y-2">
                <span className="text-[10px] text-purple-400 uppercase font-black tracking-wider block">Licence Terms:</span>
                <div className="flex flex-wrap gap-1.5">
                  {['ALL', 'Personal Use', 'Business Use', 'Single Client Use', 'Extended Licence', 'Agency Licence'].map((lic) => (
                    <button
                      key={lic}
                      onClick={() => setSelectedLicence(lic)}
                      className={`py-1 px-2.5 rounded text-[10px] border cursor-pointer ${selectedLicence === lic ? 'bg-purple-500/10 text-[#e840f3] border-purple-500/40' : 'bg-black/30 border-zinc-900 text-zinc-400 hover:text-slate-200'}`}
                    >
                      {lic.replace(' Licence', '')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Watchlist Filter toggle */}
              <div className="space-y-2">
                <span className="text-[10px] text-pink-400 uppercase font-black tracking-wider block">Watchlist Controls:</span>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedDifficulty('ALL');
                    setSelectedLicence('ALL');
                    setSortBy('priority');
                    onAddLog('Nexus Marketplace', 'Filters reset to baseline configuration.', 'SUCCESS', 'Reset parameters dashboard.');
                  }}
                  className="w-full py-2 bg-zinc-950 tracking-wider text-[10px] uppercase font-bold text-center border cursor-pointer border-zinc-850 hover:bg-zinc-900 hover:text-white rounded"
                >
                  Reset All Filters
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 5. ADMIN MANAGE PANEL PORTAL */}
      {isAdmin && showAdminPanel && (
        <div className="space-y-6">
          <NexusDigitalProductFactory
            products={products}
            setProducts={setProducts}
            onAddLog={onAddLog}
            onAddTaskToWorkSuite={onAddTaskToWorkSuite}
            publicInquiries={publicInquiries}
            setPublicInquiries={setPublicInquiries}
          />

          <div className="bg-[#0b0416] border border-[#e840f3]/40 rounded-xl p-6 relative shadow-2xl space-y-6">
            <div className="absolute top-3 right-3 flex items-center gap-1">
              <span className="text-[9px] font-mono text-zinc-500 uppercase">SYS MANAGER ACTIVE</span>
              <button
                onClick={() => {
                  setShowAdminPanel(false);
                  resetAdminForm();
                }}
                className="p-1 text-zinc-500 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <h3 className="text-sm font-mono font-black uppercase text-purple-300 tracking-widest border-b border-[#e840f3]/20 pb-2 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-[#e840f3]" />
              {adminIsEditing ? `Edit Repository: [${adminForm.productName}]` : 'Add New Digital Product Package'}
            </h3>

          <form onSubmit={handleAdminFormSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 text-xs font-mono">
            {/* Name */}
            <div className="space-y-1.5">
              <label className="text-purple-400 uppercase font-black text-[9px]">Product Name:*</label>
              <input
                type="text"
                required
                value={adminForm.productName || ''}
                onChange={(e) => setAdminForm({ ...adminForm, productName: e.target.value })}
                className="w-full bg-black/60 border border-purple-950/40 rounded p-2 text-white"
                placeholder="e.g. AI Business Website Starter"
              />
            </div>

            {/* Product Type */}
            <div className="space-y-1.5">
              <label className="text-purple-400 uppercase font-black text-[9px]">Product Type:*</label>
              <select
                value={adminForm.productType}
                onChange={(e: any) => setAdminForm({ ...adminForm, productType: e.target.value })}
                className="w-full bg-black/60 border border-purple-950/40 rounded p-2 text-white"
              >
                <option value="Ready-Made Website">Ready-Made Website</option>
                <option value="Ready-Made Mobile App">Ready-Made Mobile App</option>
                <option value="AI Assistant Template">AI Assistant Template</option>
                <option value="Automation Workflow">Automation Workflow</option>
                <option value="Make.com Workflow">Make.com Workflow</option>
                <option value="n8n Workflow">n8n Workflow</option>
                <option value="Code Pack">Code Pack</option>
                <option value="Dashboard Template">Dashboard Template</option>
                <option value="Social Media Pack">Social Media Pack</option>
                <option value="Nexus Bundle">Nexus Bundle</option>
              </select>
            </div>

            {/* Category */}
            <div className="space-y-1.5">
              <label className="text-purple-400 uppercase font-black text-[9px]">Category Name:*</label>
              <input
                type="text"
                required
                value={adminForm.category || ''}
                onChange={(e) => setAdminForm({ ...adminForm, category: e.target.value })}
                className="w-full bg-black/60 border border-purple-950/40 rounded p-2 text-white"
                placeholder="e.g. SaaS Landing Page / Driving School"
              />
            </div>

            {/* Image URL */}
            <div className="space-y-1.5 col-span-1 md:col-span-2">
              <label className="text-purple-400 uppercase font-black text-[9px]">Preview Image link:*</label>
              <input
                type="text"
                required
                value={adminForm.previewImage || ''}
                onChange={(e) => setAdminForm({ ...adminForm, previewImage: e.target.value })}
                className="w-full bg-black/60 border border-purple-950/40 rounded p-2 text-white"
              />
            </div>

            {/* Demo URL */}
            <div className="space-y-1.5">
              <label className="text-purple-400 uppercase font-black text-[9px]">Demo Site URL:</label>
              <input
                type="text"
                value={adminForm.demoUrl || ''}
                onChange={(e) => setAdminForm({ ...adminForm, demoUrl: e.target.value })}
                className="w-full bg-black/60 border border-purple-950/40 rounded p-2 text-white"
                placeholder="https://example.com/demo"
              />
            </div>

            {/* Price Tags */}
            <div className="space-y-1.5">
              <label className="text-purple-400 uppercase font-black text-[9px]">Template purchase cost (£):</label>
              <input
                type="number"
                value={adminForm.productPrice || 0}
                onChange={(e) => setAdminForm({ ...adminForm, productPrice: parseFloat(e.target.value) || 0 })}
                className="w-full bg-black/60 border border-purple-950/40 rounded p-2 text-white"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-purple-400 uppercase font-black text-[9px]">Done-for-you Setup fee (£):</label>
              <input
                type="number"
                value={adminForm.setupPrice || 0}
                onChange={(e) => setAdminForm({ ...adminForm, setupPrice: parseFloat(e.target.value) || 0 })}
                className="w-full bg-black/60 border border-purple-950/40 rounded p-2 text-white"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-purple-400 uppercase font-black text-[9px]">Monthly Support fee (£/mo):</label>
              <input
                type="number"
                value={adminForm.monthlySupportPrice || 0}
                onChange={(e) => setAdminForm({ ...adminForm, monthlySupportPrice: parseFloat(e.target.value) || 0 })}
                className="w-full bg-black/60 border border-purple-950/40 rounded p-2 text-white"
              />
            </div>

            {/* Short Description */}
            <div className="space-y-1.5 col-span-1 md:col-span-3">
              <label className="text-purple-400 uppercase font-black text-[9px]">Short Description Header (Card view):</label>
              <input
                type="text"
                value={adminForm.shortDescription || ''}
                onChange={(e) => setAdminForm({ ...adminForm, shortDescription: e.target.value })}
                className="w-full bg-black/60 border border-purple-950/40 rounded p-2 text-white"
                placeholder="Keep brief..."
              />
            </div>

            {/* Full description */}
            <div className="space-y-1.5 col-span-1 md:col-span-3">
              <label className="text-purple-400 uppercase font-black text-[9px]">Comprehensive Full Description (Details Dialog):</label>
              <textarea
                value={adminForm.fullDescription || ''}
                onChange={(e) => setAdminForm({ ...adminForm, fullDescription: e.target.value })}
                className="w-full bg-black/60 border border-purple-950/40 rounded p-2 text-white min-h-[80px]"
                placeholder="Describe features, pages included..."
              />
            </div>

            {/* Metadata selections */}
            <div className="space-y-1.5">
              <label className="text-[#00ffff] uppercase font-black text-[9px]">Setup Difficulty:*</label>
              <select
                value={adminForm.setupDifficulty}
                onChange={(e: any) => setAdminForm({ ...adminForm, setupDifficulty: e.target.value })}
                className="w-full bg-black/60 border border-[#00ffff]/30 rounded p-2 text-white"
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Done-For-You Recommended">Done-For-You Recommended</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[#00ffff] uppercase font-black text-[9px]">Delivery Method:*</label>
              <select
                value={adminForm.deliveryMethod}
                onChange={(e: any) => setAdminForm({ ...adminForm, deliveryMethod: e.target.value })}
                className="w-full bg-black/60 border border-[#00ffff]/30 rounded p-2 text-white"
              >
                <option value="Digital Download">Digital Download</option>
                <option value="Client Dashboard Delivery">Client Dashboard Delivery</option>
                <option value="Nexus Hosting Setup">Nexus Hosting Setup</option>
                <option value="GitHub / ZIP Export">GitHub / ZIP Export</option>
                <option value="Workflow JSON Export">Workflow JSON Export</option>
                <option value="Manual Setup Required">Manual Setup Required</option>
                <option value="Done-For-You Setup">Done-For-You Setup</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[#00ffff] uppercase font-black text-[9px]">Licence Type:*</label>
              <select
                value={adminForm.licenceType}
                onChange={(e: any) => setAdminForm({ ...adminForm, licenceType: e.target.value })}
                className="w-full bg-black/60 border border-[#00ffff]/30 rounded p-2 text-white"
              >
                <option value="Personal Use">Personal Use</option>
                <option value="Business Use">Business Use</option>
                <option value="Single Client Use">Single Client Use</option>
                <option value="Extended Licence">Extended Licence</option>
                <option value="Agency Licence">Agency Licence</option>
                <option value="Custom Licence">Custom Licence</option>
              </select>
            </div>

            {/* Status indicators */}
            <div className="space-y-1.5">
              <label className="text-rose-400 uppercase font-black text-[9px]">Product Catalog Status:</label>
              <select
                value={adminForm.productStatus}
                onChange={(e: any) => setAdminForm({ ...adminForm, productStatus: e.target.value })}
                className="w-full bg-black/60 border border-rose-950/40 rounded p-2 text-white"
              >
                <option value="Draft">Draft</option>
                <option value="Preview Ready">Preview Ready</option>
                <option value="Published">Published</option>
                <option value="Needs Review">Needs Review</option>
                <option value="Coming Soon">Coming Soon</option>
                <option value="Disabled">Disabled</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-rose-400 uppercase font-black text-[9px]">Build Readiness Level:</label>
              <select
                value={adminForm.buildStatus}
                onChange={(e: any) => setAdminForm({ ...adminForm, buildStatus: e.target.value })}
                className="w-full bg-black/60 border border-rose-950/40 rounded p-2 text-white"
              >
                <option value="Template Ready">Template Ready</option>
                <option value="Setup Required">Setup Required</option>
                <option value="Needs Testing">Needs Testing</option>
                <option value="Public Preview Ready">Public Preview Ready</option>
                <option value="Production Ready After Review">Production Ready After Review</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-rose-400 uppercase font-black text-[9px]">Approval Gates:</label>
              <select
                value={adminForm.approvalStatus}
                onChange={(e: any) => setAdminForm({ ...adminForm, approvalStatus: e.target.value })}
                className="w-full bg-black/60 border border-rose-950/40 rounded p-2 text-white"
              >
                <option value="Admin Draft">Admin Draft</option>
                <option value="Internal Review">Internal Review</option>
                <option value="Approved for Preview">Approved for Preview</option>
                <option value="Approved for Sale">Approved for Sale</option>
                <option value="Paused">Paused</option>
              </select>
            </div>

            {/* ADMIN ONLY PRIVATE CHANNELS (Strictly hidden from public catalogs) */}
            <div className="col-span-1 md:col-span-3 border-t border-purple-950/40 pt-4.5 space-y-4">
              <span className="text-[10px] text-pink-500 font-extrabold uppercase bg-pink-950/35 border border-pink-900/50 px-3 py-1 rounded-full">
                🔐 Administrator Private Fiscal &amp; Spec Fields
              </span>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4.5 text-[11px]">
                <div className="space-y-1.5">
                  <label className="text-indigo-400 uppercase font-bold text-[9px] block">Internal Developer Cost (£):</label>
                  <input
                    type="number"
                    value={adminForm.internalCost || 0}
                    onChange={(e) => setAdminForm({ ...adminForm, internalCost: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-purple-950/15 border border-purple-900/40 rounded p-2 text-[#e840f3] placeholder-zinc-600"
                    placeholder="Internal developer time cost"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-indigo-400 uppercase font-bold text-[9px] block">Margin Estimate Index (%):</label>
                  <input
                    type="number"
                    value={adminForm.marginEstimate || 0}
                    onChange={(e) => setAdminForm({ ...adminForm, marginEstimate: parseInt(e.target.value) || 0 })}
                    className="w-full bg-purple-950/15 border border-purple-900/40 rounded p-2 text-[#00ffff]"
                    placeholder="Calculated revenue margins"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-indigo-400 uppercase font-bold text-[9px] block">Supplier / Provider Account:</label>
                  <input
                    type="text"
                    value={adminForm.providerNotes || ''}
                    onChange={(e) => setAdminForm({ ...adminForm, providerNotes: e.target.value })}
                    className="w-full bg-purple-950/15 border border-purple-900/40 rounded p-2 text-white"
                    placeholder="e.g. Hostinger, AWS, Vercel APIs"
                  />
                </div>

                <div className="space-y-1.5 col-span-1 md:col-span-3">
                  <label className="text-indigo-400 uppercase font-bold text-[9px] block">Private Technical Build &amp; Risk Scribing Notes:</label>
                  <textarea
                    value={adminForm.buildNotes || ''}
                    onChange={(e) => setAdminForm({ ...adminForm, buildNotes: e.target.value })}
                    className="w-full bg-purple-950/15 border border-purple-900/40 rounded p-2 text-white min-h-[60px]"
                    placeholder="e.g. Needs testing container v2.1. Do not release client keys until VAT is registered."
                  />
                </div>
              </div>
            </div>

            {/* Custom file array helpers */}
            <div className="col-span-1 md:col-span-3 flex justify-between gap-4 pt-2">
              <button
                type="button"
                onClick={resetAdminForm}
                className="py-2.5 px-6 border cursor-pointer border-purple-950 rounded-xl text-[10px] uppercase font-bold hover:bg-purple-950/20 text-slate-400"
              >
                Clear form inputs
              </button>
              
              <button
                type="submit"
                className="py-2.5 px-8 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 cursor-pointer text-[#020202] text-[10px] tracking-widest font-black uppercase rounded-xl shadow-lg border border-emerald-500/30 flex items-center gap-1.5"
              >
                <Save className="w-4 h-4" />
                {adminIsEditing ? 'Commit modifications' : 'Publish new product'}
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      {/* 6. MAIN DIGITAL PRODUCTS CATALOG VIEW GRID */}
      <div className="space-y-6">
        <div className="flex justify-between items-center text-xs font-mono border-b border-zinc-900 pb-2">
          <span className="text-purple-400 uppercase font-black opacity-85">
            Active Catalog: {filteredProducts.length} product(s) found in standard matrix
          </span>
          {watchlist.length > 0 && (
            <span className="bg-pink-950/35 border border-pink-500/35 px-2.5 py-0.5 rounded text-pink-400 text-[10px] font-bold">
              ★ {watchlist.length} bookmarked
            </span>
          )}
        </div>

        {sortedProducts.length === 0 ? (
          <div className="glass-panel border border-zinc-900/80 p-12 text-center rounded-2xl bg-black/25">
            <Inbox className="w-12 h-12 text-zinc-600 mx-auto mb-4 animate-bounce" />
            <h4 className="text-sm font-mono font-black uppercase text-zinc-400">Zero matching nodes found</h4>
            <p className="text-xs text-zinc-500 font-sans mt-1">Try resetting the custom filters or search parameters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedProducts.map((p) => {
              const isSaved = watchlist.includes(p.id);
              return (
                <div
                  key={p.id}
                  className="glass-panel border border-zinc-900/90 hover:border-[#e840f3]/40 hover:shadow-[0_0_35px_rgba(232,64,243,0.08)] bg-black/45 rounded-2xl p-5 flex flex-col justify-between transition-all duration-300 relative group"
                >
                  {/* Glowing upper line */}
                  <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#e840f3]/10 via-[#00ffff]/20 to-[#e840f3]/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Coming Soon custom visual seal */}
                  {p.productStatus === 'Coming Soon' && (
                    <div className="absolute inset-0 bg-[#020205]/92 z-20 flex flex-col items-center justify-center p-4 rounded-2xl border border-zinc-850">
                      <Clock className="w-10 h-10 text-cyan-400 animate-pulse mb-3" />
                      <span className="text-xs uppercase font-mono font-black tracking-widest text-[#e840f3]">Coming Soon</span>
                      <span className="text-[10px] font-mono text-zinc-500 lowercase mt-1 text-center">B2B template integration in progress</span>
                      
                      <button
                        onClick={() => {
                          alert(`Tracking active. We will send a secure invoice draft once published.`);
                          onAddLog('Nexus Marketplace', `Tracking asset pre-activation: "${p.productName}"`, 'SUCCESS', 'Saved client notifications webhook.');
                        }}
                        className="mt-4 py-1.5 px-4 bg-zinc-950 border border-zinc-800 hover:border-[#00ffff]/40 rounded-xl cursor-pointer text-[9px] font-mono font-black uppercase text-cyan-300 transition-colors"
                      >
                        Track Status
                      </button>
                    </div>
                  )}

                  {/* Top card metadata */}
                  <div className="space-y-4">
                    <div className="relative rounded-xl overflow-hidden h-40 bg-zinc-950 border border-zinc-900">
                      <img
                        src={p.previewImage}
                        alt={p.productName}
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent pointer-events-none" />
                      
                      {/* Interactive bookmark */}
                      <button
                        onClick={() => handleWatchlistToggle(p.id)}
                        className="absolute top-2.5 right-2.5 p-1.5 rounded-lg bg-black/65 border border-zinc-800/80 text-zinc-400 hover:text-cyan-400 transition-colors z-10"
                      >
                        <Bookmark className={`w-3.5 h-3.5 ${isSaved ? 'fill-cyan-400 text-cyan-400' : ''}`} />
                      </button>

                      {/* Floating type tag */}
                      <div className="absolute bottom-2.5 left-2.5 bg-black/80 border border-purple-500/40 px-2 py-0.5 rounded-lg text-[8px] font-mono text-[#e840f3] uppercase font-bold">
                        {p.productType}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-[8.5px] font-mono text-zinc-500 uppercase font-bold">
                        <span>{p.category}</span>
                        <span className="text-zinc-600">Plan: {p.licenceType.replace(' Licence', '')}</span>
                      </div>

                      <h4 className="text-sm font-sans font-black text-white group-hover:text-cyan-400 tracking-tight transition-colors uppercase">
                        {p.productName}
                      </h4>

                      <p className="text-[10.5px] text-zinc-400 leading-relaxed font-sans normal-case font-medium line-clamp-3">
                        {p.shortDescription}
                      </p>
                    </div>

                    {/* Metadata boxes */}
                    <div className="grid grid-cols-2 gap-2 text-[8.5px] font-mono border-t border-b border-zinc-900/60 py-3 mt-1 text-zinc-400">
                      <div className="flex items-center gap-1.5">
                        <Cpu className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                        <span>Diff: <strong className="text-slate-200">{p.setupDifficulty}</strong></span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span>Mode: <strong className="text-slate-200">{p.deliveryMethod.replace(' Delivery', '')}</strong></span>
                      </div>
                    </div>

                    {/* Tech stack badges */}
                    <div className="flex flex-wrap gap-1 pt-1 justify-between items-center">
                      <div className="flex flex-wrap gap-1">
                        {p.techStack.slice(0, 4).map((tech) => (
                          <span key={tech} className="bg-zinc-950 border border-zinc-900 text-[8px] font-mono px-2 py-0.5 rounded text-zinc-400">
                            {tech}
                          </span>
                        ))}
                      </div>
                      
                      {/* Safety Disclaimer and Interactive Tooltip */}
                      <div className="relative group/tooltip flex items-center gap-1 text-[8.5px] font-mono text-amber-500/90 select-none cursor-help bg-amber-500/5 px-2 py-0.5 rounded border border-amber-500/10">
                        <AlertTriangle className="w-3 h-3 text-amber-500/80 animate-pulse shrink-0" />
                        <span>🛡️ Safety Notice</span>
                        
                        {/* CSS-only Tooltip overlay */}
                        <div className="absolute bottom-full right-0 mb-2 w-64 bg-zinc-950 border border-zinc-850 rounded-xl p-3 shadow-[0_10px_30px_rgba(0,0,0,0.8)] opacity-0 translate-y-1 scale-95 pointer-events-none group-hover/tooltip:opacity-100 group-hover/tooltip:translate-y-0 group-hover/tooltip:scale-100 transition-all duration-300 z-50 text-left normal-case leading-normal text-zinc-300 font-sans text-[10px]">
                          <div className="absolute bottom-0 right-4 translate-y-1/2 rotate-45 w-2 h-2 bg-zinc-950 border-r border-b border-zinc-850" />
                          <strong className="text-amber-400 font-mono text-[9px] uppercase tracking-wider block mb-1">🛡️ SYSTEM SAFETY AUDIT DIRECTIVE:</strong>
                          This digital template, code pack, or workflow asset is provided as a ready-made deployable baseline. Before production release, independent source audits, compliance reviews, and custom security configuration testing are strongly recommended to preserve database and infrastructure integrity.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing and Action Row */}
                  <div className="pt-5 mt-4 border-t border-zinc-900/60 space-y-4">
                    
                    {/* Discount badge */}
                    {p.setupPrice > 0 && (
                      <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/35 rounded-lg p-2 flex flex-col gap-0.5 text-left">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] font-mono font-black text-[#e5c158] uppercase tracking-wider">
                            ✨ Save 40% with NexoraX Managed Launch
                          </span>
                          <span className="bg-[#e5c158] text-black text-[7.5px] font-sans font-black uppercase px-1.5 py-0.5 rounded leading-none">
                            40% OFF BUNDLE
                          </span>
                        </div>
                        <span className="text-[8.5px] font-mono text-zinc-400 normal-case leading-normal">
                          Applies when choosing NexoraX-managed hosting, domains/email setup, active maintenance support &amp; Client Dashboard delivery.
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between items-end gap-2 bg-black/30 border border-zinc-950 p-3 rounded-xl">
                      {p.productPrice === 0 ? (
                        <div className="font-mono text-left w-full flex justify-between items-center">
                          <div>
                            <span className="text-[8px] text-cyan-400 uppercase block leading-none mb-1">Full System:</span>
                            <span className="text-sm font-black text-white block leading-none">Request Quote</span>
                          </div>
                          <span className="text-[8.5px] text-purple-400 block leading-none font-bold">Custom Buildout</span>
                        </div>
                      ) : (
                        <>
                          <div className="font-mono text-left">
                            <span className="text-[8px] text-zinc-500 uppercase block leading-none mb-1">Standard Setup Price:</span>
                            <span className="text-xs font-black text-slate-400 block line-through leading-none mb-1">£{p.setupPrice || 399} setup</span>
                            <span className="text-[8px] text-cyan-400 uppercase block leading-none mb-1">Managed Launch Bundle:</span>
                            <span className="text-sm font-black text-white block leading-none">£{Math.floor((p.setupPrice || 399) * 0.6)} <span className="text-[8px] text-[#00ff00] font-normal font-mono">(-40%)</span></span>
                          </div>

                          <div className="font-mono text-right border-l border-zinc-900 pl-4">
                            <span className="text-[8px] text-zinc-500 uppercase block leading-none mb-1">Digital Template:</span>
                            <span className="text-[13px] font-bold text-amber-500 tracking-tight block leading-none mb-1">
                              £{p.productPrice || 99} <span className="text-[7.5px] text-zinc-500 font-normal">one-off</span>
                            </span>
                            <span className="text-[8.5px] text-purple-400 block leading-none font-bold">£{p.monthlySupportPrice || 29}/mo plan</span>
                          </div>
                        </>
                      )}
                    </div>

                    <p className="text-[9px] font-mono text-zinc-500 text-left leading-normal italic">
                      The Managed Launch Discount applies to eligible digital products when setup, hosting, domain/email configuration, and ongoing support are managed through NexoraX. If external hosting or self-managed delivery is selected, standard template pricing applies. No hidden penalties.
                    </p>

                    {p.tags.includes("Tracking") && (
                      <div className="bg-amber-500/10 border border-amber-500/20 p-2.5 rounded-xl text-left mt-2">
                        <span className="block font-mono text-[7.5px] uppercase tracking-wide font-black text-amber-400 mb-1">⚠️ SYSTEM INTEGRATION REQUIREMENT</span>
                        <p className="font-sans text-[8.5px] text-amber-100/90 leading-tight normal-case">
                          Live driver tracking requires GPS permissions, driver/customer roles, secure backend services, map integration, privacy consent, and live data handling. Full live tracking systems are quoted separately.
                        </p>
                      </div>
                    )}

                    {/* Button CTAs */}
                    <div className="flex flex-col gap-2 font-mono text-[9px] font-black uppercase">
                      
                      <div className="grid grid-cols-2 gap-2">
                        {/* View Template */}
                        <button
                          onClick={() => {
                            if (p.demoUrl) {
                              window.open(p.demoUrl, '_blank', 'noopener,noreferrer');
                            } else {
                              alert(`Demo template is being prepared for B2B review corresponding to: "${p.productName}"`);
                            }
                          }}
                          className="py-2 px-2 bg-zinc-950 border border-zinc-850 text-slate-300 hover:text-white hover:border-[#00ffff]/40 rounded-xl cursor-pointer text-center flex items-center justify-center gap-1 transition-colors"
                        >
                          <Eye size={12} className="text-cyan-400" />
                          <span>View Template</span>
                        </button>

                        {/* Request Setup (Triggers Checkout Wizard) */}
                        <button
                          onClick={() => {
                            setActiveRequestProduct(p);
                            setRequestStep(1);
                            setRequestConfig({
                              deliveryOption: 'Managed Launch Bundle',
                              extras: {
                                hosting: true,
                                domain: true,
                                email: true,
                                support: true,
                                chat: false,
                                seo: false,
                                social: false
                              },
                              clientName: user?.displayName || '',
                              clientBusiness: '',
                              clientEmail: user?.email || '',
                              message: ''
                            });
                            onAddLog('Checkout Matrix', `Began setup request flow for: "${p.productName}"`, 'SUCCESS', 'Initialized Step 1 configuration.');
                          }}
                          className="py-2 px-2 bg-gradient-to-r from-purple-800 to-[#e840f3] text-white border border-[#e840f3]/40 rounded-xl cursor-pointer text-center flex items-center justify-center gap-1 hover:brightness-110 active:scale-[0.98] transition-all font-bold"
                        >
                          <Zap size={11} className="text-yellow-400 animate-pulse" />
                          <span>Request Setup</span>
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        {/* Request Quote */}
                        <button
                          onClick={() => {
                            onAddLog(
                              'Billing Integration',
                              `Custom Quote requested: ${p.productName}`,
                              'PENDING_APPROVAL',
                              `Custom Quote request. Template: ${p.productName}.`
                            );
                            alert(`QUOTE REQUEST SUBMITTED!\n\nOur advisory team has logged your quote request for "${p.productName}". We will contact you at ${user?.email || 'your registered address'} to review the specifications.`);
                          }}
                          className="py-2 px-2 bg-black/60 border border-zinc-850 text-zinc-300 hover:text-white hover:border-yellow-500/40 rounded-xl cursor-pointer flex items-center justify-center gap-1"
                        >
                          <Receipt size={11} className="text-yellow-500" />
                          <span>Request Quote</span>
                        </button>

                        {/* Add to Website Package */}
                        <button
                          onClick={() => {
                            onAddLog(
                              'Add-on Integration',
                              `Added to package: ${p.productName}`,
                              'SUCCESS',
                              `Client added ${p.productName} to active website build request.`
                            );
                            alert(`ADDED TO PACKAGE!\n\n"${p.productName}" has been flagged as an active add-on for your primary website package clearance. Our team will bundle this configuration in your custom proposal.`);
                          }}
                          className="py-2 px-2 bg-black/60 border border-zinc-850 text-zinc-300 hover:text-white hover:border-[#00ffff]/40 rounded-xl cursor-pointer flex items-center justify-center gap-1"
                        >
                          <Plus size={11} className="text-cyan-400" />
                          <span>Add to Package</span>
                        </button>
                      </div>

                      {/* Direct Interactive Layout Customisation CTA */}
                      <button
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('nexorax-custom-template-trigger', { detail: p.id }));
                          window.dispatchEvent(new CustomEvent('nexorax-trigger-tab-change', { detail: 'Product Builder' }));
                          onAddLog('Digital Store', `Initiated live draft customizer for template: "${p.productName}"`, 'SUCCESS', 'Redirected client layout to Active Product Builder workspace.');
                        }}
                        className="py-2.5 px-3 bg-gradient-to-r from-indigo-950 via-purple-950 to-indigo-950 hover:bg-gradient-to-r hover:from-purple-900 hover:to-indigo-900 border border-purple-500/20 hover:border-purple-400 text-purple-300 hover:text-white rounded-xl cursor-pointer text-center flex items-center justify-center gap-1.5 transition-all text-[9.5px]"
                      >
                        <Wand2 size={12} className="text-purple-400 animate-pulse" />
                        <span>Customize Layout Options First →</span>
                      </button>

                    </div>

                    {/* Admin Actions */}
                    {isAdmin && (
                      <div className="flex justify-between items-center bg-purple-950/20 border border-purple-900/40 rounded-lg p-2 mt-1 text-[9px]">
                        <span className="text-zinc-500">ADMIN UTILS:</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditProduct(p)}
                            className="text-purple-300 hover:text-white flex items-center gap-0.5 cursor-pointer"
                          >
                            <Edit3 className="w-3 h-3" /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(p.id, p.productName)}
                            className="text-rose-400 hover:text-rose-200 flex items-center gap-0.5 cursor-pointer"
                          >
                            <Trash2 className="w-3 h-3" /> Discard
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 7. IMMERSIVE COMPONENT WIZARDS (MODALS & POPUPS) */}
      
      {/* A. PRODUCT DETAILS MODAL */}
      {selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm overflow-y-auto">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#04040a] border border-[#e840f3]/30 rounded-2xl p-6 max-w-4xl w-full max-h-[85vh] overflow-y-auto relative text-xs font-mono space-y-6"
          >
            <button
              onClick={() => setSelectedProduct(null)}
              className="absolute top-4 right-4 p-1.5 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Title / Banner Image */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-1 rounded-xl overflow-hidden h-44 border border-zinc-900">
                <img
                  src={selectedProduct.previewImage}
                  alt={selectedProduct.productName}
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="md:col-span-2 flex flex-col justify-between space-y-2">
                <div>
                  <span className="text-[9px] uppercase tracking-wider text-[#e840f3] bg-[#e840f3]/10 border border-[#e840f3]/30 px-2 py-0.5 rounded">
                    {selectedProduct.productType}
                  </span>
                  <h2 className="text-xl font-sans font-black text-white uppercase mt-2">{selectedProduct.productName}</h2>
                  <p className="text-[10px] text-zinc-400 italic mt-1">{selectedProduct.category}</p>
                </div>

                <div className="bg-black/50 border border-zinc-900 p-3 rounded-lg flex justify-between">
                  <div>
                    <span className="text-[8px] text-zinc-500 block uppercase">Product Price:</span>
                    <strong className="text-[#00ffff] text-sm">£{selectedProduct.productPrice} one-off</strong>
                  </div>
                  <div>
                    <span className="text-[8px] text-zinc-500 block uppercase">Setup / Support:</span>
                    <strong className="text-[#e840f3] text-sm">£{selectedProduct.setupPrice} setup</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Description & Included specs */}
            <div className="space-y-3">
              <span className="text-[10px] text-cyan-300 font-bold tracking-wider uppercase block border-b border-zinc-900 pb-1">
                Product Summary &amp; B2B Capabilities
              </span>
              <p className="text-zinc-300 font-sans leading-relaxed text-xs capitalize-none">
                {selectedProduct.fullDescription}
              </p>
            </div>

            {/* Specifications Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[10px] text-zinc-400 bg-[#060714] p-4.5 rounded-lg border border-zinc-900">
              <div className="space-y-1.5">
                <p>⚙️ <strong className="text-slate-200">Tech Stack:</strong> {selectedProduct.techStack.join(', ')}</p>
                <p>🔌 <strong className="text-slate-200">Available Integrations:</strong> {selectedProduct.integrations.join(', ') || 'N/A'}</p>
                <p>📍 <strong className="text-slate-200">Delivery Vector:</strong> {selectedProduct.deliveryMethod}</p>
              </div>
              <div className="space-y-1.5 text-right md:text-left">
                <p>🛡️ <strong className="text-slate-200">Licence Terms:</strong> {selectedProduct.licenceType}</p>
                <p>📊 <strong className="text-slate-200">Setup Complexity:</strong> {selectedProduct.setupDifficulty}</p>
                <p>🔋 <strong className="text-slate-200">Deploy Readiness:</strong> {selectedProduct.buildStatus}</p>
              </div>
            </div>

            {/* Included code files list */}
            <div className="space-y-2">
              <span className="text-[10px] text-purple-400 font-bold tracking-wider uppercase block">
                Included Repository File Tree Blueprint:
              </span>
              <div className="bg-black border border-purple-950/60 p-3 rounded-md font-mono text-[10.5px] text-[#00ff00]">
                {selectedProduct.includedFiles.map(file => (
                  <p key={file}>📄 <span className="text-white">{file}</span></p>
                ))}
              </div>
            </div>

            {/* Direct Warning / Safety Audit Callout Panel */}
            <div className="bg-amber-950/15 border border-amber-900/30 p-4 rounded-xl flex gap-3 text-left">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5 animate-pulse" />
              <div className="text-[10.5px] text-zinc-400 font-sans leading-relaxed normal-case">
                <span className="text-amber-500 font-mono font-black uppercase text-[9.5px] block mb-1">
                  🛡️ B2B CODE-BASE SAFETY &amp; TESTING GUIDELINES:
                </span>
                This template, workflow schema, or code pack file structure is provided as a pre-built static baseline. Post deployment, direct configuration, environment adjustments, and database schema mappings are required. We strongly recommend independent safety audits, security architecture evaluation, and comprehensive vulnerability scanning before putting any code asset into production routing.
              </div>
            </div>

            {/* Inside details page footer */}
            <div className="flex flex-wrap gap-3 justify-end pt-3 border-t border-zinc-900">
              <button
                onClick={() => setSelectedProduct(null)}
                className="py-2.5 px-5 bg-zinc-950 border border-zinc-800 text-slate-300 rounded-xl cursor-pointer font-bold uppercase text-[10px]"
              >
                Close specs
              </button>
              
              <button
                onClick={() => {
                  handleRequestSetup(selectedProduct);
                  setSelectedProduct(null);
                }}
                className="py-2.5 px-6 bg-[#e840f3] hover:brightness-110 text-white rounded-xl cursor-pointer font-black uppercase text-[10px] tracking-wide flex items-center gap-1"
              >
                <Receipt size={13} />
                <span>Request Custom Setup Package</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* B. READY-MADE WEBSITE BUILDER FLOW */}
      {websiteBuilderProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md overflow-y-auto font-mono text-xs text-slate-300">
          <motion.div
            initial={{ y: 25, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-[#030308] border border-[#00ffff]/40 rounded-2xl p-6 max-w-2xl w-full relative space-y-6"
          >
            <button
              onClick={() => setWebsiteBuilderProduct(null)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div>
              <span className="text-[#00ffff] bg-[#00ffff]/5 border border-[#00ffff]/30 px-2 py-0.5 rounded uppercase font-bold text-[8.5px]">
                NEX_WEB_BUILDER_WIZARD_v1.9
              </span>
              <h2 className="text-lg font-sans font-black text-white uppercase mt-2">
                Configure Website Setup: {websiteBuilderProduct.productName}
              </h2>
            </div>

            {/* Step metrics */}
            <div className="flex items-center gap-2 border-b border-zinc-900 pb-3">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center gap-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] ${websiteBuilderStep === step ? 'bg-[#00ffff] text-black' : websiteBuilderStep > step ? 'bg-[#e840f3] text-white' : 'bg-zinc-900 text-zinc-500'}`}>
                    {step}
                  </div>
                  <span className={`text-[9px] uppercase font-bold ${websiteBuilderStep === step ? 'text-white' : 'text-zinc-500'}`}>
                    {step === 1 ? 'Configure Option' : step === 2 ? 'Add-on Packages' : 'Submit Settlement'}
                  </span>
                  {step < 3 && <span className="text-zinc-700">/</span>}
                </div>
              ))}
            </div>

            {/* Step Content */}
            {websiteBuilderStep === 1 && (
              <div className="space-y-4">
                <p className="text-zinc-400">Select the baseline delivery schema for this website template package:</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setWebsiteConfig({ ...websiteConfig, builderType: 'template' })}
                    className={`p-4 border text-left rounded-xl transition-all cursor-pointer ${websiteConfig.builderType === 'template' ? 'bg-[#e840f3]/5 border-[#e840f3]/70 shadow-[0_0_10px_rgba(232,64,243,0.15)]' : 'bg-black/40 border-zinc-900 hover:border-zinc-800'}`}
                  >
                    <FileText className="w-6 h-6 text-[#e840f3] mb-2" />
                    <strong className="text-white block uppercase text-[10px]">Download Template Only</strong>
                    <span className="text-[9px] text-zinc-500 block mt-1 leading-normal">Get the complete clean code pack immediately for self-hosting. (Cost £{websiteBuilderProduct.productPrice})</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setWebsiteConfig({ ...websiteConfig, builderType: 'done-for-you' })}
                    className={`p-4 border text-left rounded-xl transition-all cursor-pointer ${websiteConfig.builderType === 'done-for-you' ? 'bg-cyan-500/5 border-cyan-500/70 shadow-[0_0_10px_rgba(6,182,212,0.15)]' : 'bg-black/40 border-zinc-900 hover:border-zinc-800'}`}
                  >
                    <Zap className="w-6 h-6 text-cyan-400 mb-2" />
                    <strong className="text-white block uppercase text-[10px]">DFY Standard Installation</strong>
                    <span className="text-[9px] text-zinc-500 block mt-1 leading-normal">Our engineers handle Docker deployment, SSL certificates and DNS setups. (Cost £{websiteBuilderProduct.setupPrice} setup)</span>
                  </button>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] uppercase font-bold text-slate-400 block">Website Category Blueprint:</label>
                  <select
                    value={websiteConfig.subCategory}
                    onChange={(e) => setWebsiteConfig({ ...websiteConfig, subCategory: e.target.value })}
                    className="w-full bg-black border border-zinc-900 rounded p-2.5 text-white"
                  >
                    <option value="Business Website">Business Website Theme</option>
                    <option value="Restaurant / Takeaway">Restaurant &amp; Takeaway Layout</option>
                    <option value="Driving School">Driving School Layout</option>
                    <option value="Beauty / Salon">Beauty &amp; Salon Landing Page</option>
                    <option value="Trades / Construction">Trades &amp; Local Construction Grid</option>
                    <option value="Real Estate">Real Estate Listings Starter</option>
                    <option value="Consultant / Agency">Marketing Consultant &amp; B2B Agency</option>
                    <option value="E-commerce">E-commerce with Shopping Cart Grid</option>
                    <option value="Booking Website">Booking Website Scheduler</option>
                    <option value="SaaS Landing Page">SaaS &amp; Tech Launch Landing Page</option>
                  </select>
                </div>
              </div>
            )}

            {websiteBuilderStep === 2 && (
              <div className="space-y-4">
                <p className="text-zinc-400">Expand your system footprint with automated B2B services (Optional):</p>
                
                <div className="space-y-3">
                  {[
                    { id: 'hosting', label: 'Managed Hosting', desc: 'Provides auto-updating system setup, static SSL protection, and daily snapshots.', price: '£29/month plan', stateKey: 'addHosting' },
                    { id: 'domain', label: 'Registered Domain Name (.co.uk / .com)', desc: 'Includes domain setup and nameservers validation.', price: 'From £12 one-off', stateKey: 'addDomain' },
                    { id: 'mail', label: 'Secure Google Workspace (@nexorax.co.uk equivalent)', desc: 'Enterprise communications inbox with anti-spam relay configurations.', price: 'From £9/user-month', stateKey: 'addMail' },
                    { id: 'seo', label: 'Organic SEO Optimization Setup', desc: 'Sitemaps layout indexation, keywords research, speed optimizations.', price: '£149 single campaign', stateKey: 'addSEO' }
                  ].map((srv) => (
                    <label key={srv.id} className="flex justify-between items-start gap-3 bg-black/50 border border-zinc-900/60 p-3 rounded-lg hover:border-zinc-800 cursor-pointer">
                      <div className="flex gap-2.5 items-start">
                        <input
                          type="checkbox"
                          checked={(websiteConfig as any)[srv.stateKey]}
                          onChange={(e) => setWebsiteConfig({ ...websiteConfig, [srv.stateKey]: e.target.checked })}
                          className="mt-1"
                        />
                        <div>
                          <strong className="text-white text-[10.5px] block">{srv.label}</strong>
                          <span className="text-[9.5px] text-zinc-500 block leading-tight mt-0.5">{srv.desc}</span>
                        </div>
                      </div>
                      <span className="text-[10px] text-cyan-400 font-bold shrink-0">{srv.price}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {websiteBuilderStep === 3 && (
              <div className="space-y-4 font-mono text-xs">
                <p className="text-zinc-400">Estimate Pricing Summary under Direct B2B billing records:</p>
                
                <div className="bg-[#050611] p-4 rounded-xl border border-zinc-900 space-y-2.5">
                  <div className="flex justify-between">
                    <span>Selected Template:</span>
                    <span className="text-white font-bold">{websiteBuilderProduct.productName}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Base Setup Type:</span>
                    <span className="text-white font-bold">{websiteConfig.builderType === 'done-for-you' ? 'DFY Onboarding Setup' : 'Code Download Only'}</span>
                  </div>

                  <div className="flex justify-between text-[#00ffff]">
                    <span>Standard Billing Fee:</span>
                    <span>{websiteConfig.builderType === 'done-for-you' ? `£${websiteBuilderProduct.setupPrice}` : `£${websiteBuilderProduct.productPrice}`}</span>
                  </div>

                  {websiteConfig.addHosting && (
                    <div className="flex justify-between">
                      <span>Hosting Subscription Add-on:</span>
                      <span className="text-purple-400">£29.00 / month</span>
                    </div>
                  )}

                  {websiteConfig.addSEO && (
                    <div className="flex justify-between">
                      <span>SEO Campaign Setup:</span>
                      <span className="text-amber-500">£149.00 one-off</span>
                    </div>
                  )}

                  <div className="border-t border-zinc-900 pt-2 flex justify-between font-bold text-sm">
                    <span className="text-white uppercase tracking-wider">Estimated Invoice Total:</span>
                    <span className="text-yellow-400">
                      £{
                        (websiteConfig.builderType === 'done-for-you' ? websiteBuilderProduct.setupPrice : websiteBuilderProduct.productPrice) +
                        (websiteConfig.addSEO ? 149 : 0)
                      }.00 + VAT
                    </span>
                  </div>
                </div>

                <div className="bg-black/50 border border-zinc-900 p-2.5 rounded text-[10px] text-zinc-500 leading-normal">
                  Invoice with statutory VAT breakdown will be queued and sent to your verified registered address. Administrative settlement terms: 14 days. Zero-Fund billing credits schemeNI: NH448796B applied where compatible.
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between gap-4 pt-2 border-t border-zinc-900">
              <button
                type="button"
                disabled={websiteBuilderStep === 1}
                onClick={() => setWebsiteBuilderStep(prev => prev - 1)}
                className="py-2.5 px-5 bg-zinc-950 border border-zinc-850 rounded-xl cursor-pointer hover:bg-zinc-900 hover:text-white disabled:opacity-20"
              >
                Back Coordinates
              </button>

              {websiteBuilderStep < 3 ? (
                <button
                  type="button"
                  onClick={() => setWebsiteBuilderStep(prev => prev + 1)}
                  className="py-2.5 px-6 bg-[#e840f3] hover:brightness-110 text-white rounded-xl cursor-pointer uppercase font-black"
                >
                  Configure Options
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    const descText = `Website config: DFY standard setup. Addons: Hosting ${websiteConfig.addHosting}, SEO ${websiteConfig.addSEO}, Category: ${websiteConfig.subCategory}.`;
                    handleRequestSetup(websiteBuilderProduct, descText);
                    setWebsiteBuilderProduct(null);
                  }}
                  className="py-2.5 px-6 bg-[#00ffff] hover:brightness-110 text-[#020202] rounded-xl cursor-pointer uppercase font-black tracking-widest"
                >
                  Execute &amp; Issue Invoice
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* C. READY-MADE MOBILE APP BUILDER FLOW */}
      {appBuilderProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-md overflow-y-auto font-mono text-xs text-slate-300">
          <motion.div
            initial={{ y: 25, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-[#030308] border border-[#e840f3]/45 rounded-2xl p-6 max-w-2xl w-full relative space-y-6"
          >
            <button
              onClick={() => setAppBuilderProduct(null)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header / App category */}
            <div>
              <span className="text-[#e840f3] bg-[#e840f3]/10 border border-[#e840f3]/30 px-3 py-1 rounded-full uppercase font-bold text-[8.5px]">
                📱 NEXUS MOBILE CORE BUILDER PROTOCOL
              </span>
              <h2 className="text-lg font-sans font-black text-white uppercase mt-2">
                Launch App Template: {appBuilderProduct.productName}
              </h2>
            </div>

            {/* Step indicators */}
            <div className="flex gap-2 border-b border-zinc-900 pb-3">
              {[1, 2].map((step) => (
                <div key={step} className="flex items-center gap-2">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] ${appBuilderStep === step ? 'bg-[#e840f3] text-white' : 'bg-zinc-900 text-zinc-500'}`}>
                    {step}
                  </span>
                  <span className={`text-[9px] uppercase font-bold ${appBuilderStep === step ? 'text-white' : 'text-zinc-500'}`}>
                    {step === 1 ? 'Configure Core Stack' : 'Build Summary'}
                  </span>
                </div>
              ))}
            </div>

            {appBuilderStep === 1 ? (
              <div className="space-y-4">
                <p className="text-zinc-400">Choose compilation settings and backend servers:</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-purple-400 uppercase font-black text-[9px] block">App Compilation Stack:</label>
                    <select
                      value={appConfig.techStack}
                      onChange={(e) => setAppConfig({ ...appConfig, techStack: e.target.value })}
                      className="w-full bg-black border border-zinc-900 rounded p-2.5 text-white focus:outline-none"
                    >
                      <option value="React Native + Tailwind Expo">React Native + Expo Dev CLI (iOS/Android)</option>
                      <option value="Flutter + Dart">Flutter + Dart Native Container</option>
                      <option value="Next.js PWA WebView">NextJS Progressive Web App Core</option>
                      <option value="Swift / Kotlin Native">Swift/Kotlin native templates packs</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-purple-400 uppercase font-black text-[9px] block">Database &amp; Engine Storage Area:</label>
                    <select
                      value={appConfig.hostingBackend}
                      onChange={(e) => setAppConfig({ ...appConfig, hostingBackend: e.target.value })}
                      className="w-full bg-black border border-zinc-900 rounded p-2.5 text-white focus:outline-none"
                    >
                      <option value="Node Server + Firestore Container">Node Server + Firebase Firestore DB</option>
                      <option value="Supabase relational PostgreSQL">Relational PostgreSQL (Supabase Engine)</option>
                      <option value="Local SQLite Encrypted Storage">Offline SQLite with device-only sync</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] uppercase font-bold text-slate-400 block pb-1">Technical inclusions:</label>
                  
                  <label className="flex justify-between items-center bg-black/50 border border-zinc-900 p-3 rounded-lg hover:border-zinc-800 cursor-pointer">
                    <div>
                      <strong className="text-white text-[10.5px] block">Pre-configured Admin Panel Dashboard</strong>
                      <span className="text-[9.5px] text-zinc-500 block">Deploy web-based metrics controls for staff queue management.</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={appConfig.includeAdminPanel}
                      onChange={(e) => setAppConfig({ ...appConfig, includeAdminPanel: e.target.checked })}
                    />
                  </label>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-zinc-400">Review your compilation specifications:</p>

                <div className="bg-[#050611] p-4 rounded-xl border border-zinc-900 space-y-2.5 text-xs">
                  <div className="flex justify-between">
                    <span>Base Template:</span>
                    <strong className="text-white">{appBuilderProduct.productName}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Language / Runtime SDK:</span>
                    <strong className="text-cyan-400">{appConfig.techStack}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Database Engine:</span>
                    <strong className="text-[#e840f3]">{appConfig.hostingBackend}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Include Admin HUD:</span>
                    <strong className="text-white">{appConfig.includeAdminPanel ? 'Yes (Connected)' : 'No'}</strong>
                  </div>
                  <div className="border-t border-zinc-950 pt-2 flex justify-between font-bold text-[#00ffff]">
                    <span>Setup Fee:</span>
                    <span>£{appBuilderProduct.setupPrice} one-off</span>
                  </div>
                </div>
              </div>
            )}

            {/* App Screen mockups presentation */}
            <div className="bg-black/80 border border-zinc-900 rounded-xl p-4 flex gap-4 overflow-x-auto select-none pointer-events-none">
              {[
                { name: 'Onboarding Screen', path: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=200' },
                { name: 'Core Dashboard Screen', path: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=200' },
                { name: 'CRM Request Grid', path: 'https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&q=80&w=200' }
              ].map((s, idx) => (
                <div key={idx} className="shrink-0 w-32 border border-zinc-800 rounded-lg overflow-hidden h-40 relative">
                  <img src={s.path} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 bg-black/80 p-1.5 text-[8px] text-center text-zinc-400">{s.name}</div>
                </div>
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="flex justify-between gap-4 pt-2 border-t border-zinc-900">
              <button
                type="button"
                disabled={appBuilderStep === 1}
                onClick={() => setAppBuilderStep(1)}
                className="py-2.5 px-5 bg-zinc-950 border border-zinc-850 rounded-xl cursor-pointer hover:bg-zinc-900 hover:text-white"
              >
                Go back
              </button>

              {appBuilderStep === 1 ? (
                <button
                  type="button"
                  onClick={() => setAppBuilderStep(2)}
                  className="py-2.5 px-6 bg-[#e840f3] hover:brightness-110 text-white rounded-xl cursor-pointer uppercase font-black"
                >
                  Review Summary
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    const descText = `App config: Compile targeting ${appConfig.techStack}, DB ${appConfig.hostingBackend}. Admin panel included: ${appConfig.includeAdminPanel}.`;
                    handleRequestSetup(appBuilderProduct, descText);
                    setAppBuilderProduct(null);
                  }}
                  className="py-2.5 px-6 bg-[#e840f3] hover:brightness-110 text-white rounded-xl cursor-pointer uppercase font-black tracking-widest"
                >
                  Compile App &amp; Onboard
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* D. WORKFLOW TEMPLATE PREVIEW POPUP */}
      {activeWorkflowProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md overflow-y-auto font-mono text-xs text-slate-300">
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="bg-[#030308] border border-cyan-500/40 rounded-2xl p-6 max-w-2xl w-full relative space-y-6"
          >
            <button
              onClick={() => setActiveWorkflowProduct(null)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <div>
              <span className="text-cyan-400 bg-cyan-900/10 border border-cyan-500/35 px-2 py-0.5 rounded font-bold uppercase text-[9px]">
                🔗 B2B WORKFLOW PIPELINE ARCHITECTURE
              </span>
              <h2 className="text-lg font-sans font-black text-white uppercase mt-2">{activeWorkflowProduct.productName}</h2>
            </div>

            <p className="text-zinc-400 capitalize-none">{activeWorkflowProduct.shortDescription}</p>

            {/* Pipeline flowchart mapping */}
            <div className="space-y-3 bg-black border border-zinc-900 p-4 rounded-xl relative">
              <div className="flex flex-col items-center gap-3">
                <div className="bg-purple-950/40 border border-purple-500/30 p-2.5 rounded-lg w-full text-center">
                  <span className="text-[8px] text-zinc-500 block uppercase font-bold">1. TRIGGER (Incoming webhook)</span>
                  <strong className="text-white text-[11px]">Database records mutation / webhook capture</strong>
                </div>

                <div className="w-0.5 h-4 bg-cyan-500/50" />

                <div className="bg-[#050611] border border-cyan-500/35 p-2.5 rounded-lg w-full text-center">
                  <span className="text-[8px] text-zinc-400 block uppercase font-bold">2. LOGIC (n8n / Make integration Node)</span>
                  <strong className="text-cyan-300 text-[11px] block text-center">Branching: AI Drafting &amp; Safety Compliance reviews</strong>
                </div>

                <div className="w-0.5 h-4 bg-cyan-500/50" />

                <div className="bg-yellow-500/5 border border-yellow-500/20 p-2 rounded-lg w-5/6 text-center text-[10px] text-yellow-500 font-sans leading-tight">
                  ⚠️ Human approval checkpoint. Safe verify validation before external API outreach actions are enabled.
                </div>

                <div className="w-0.5 h-4 bg-cyan-500/50" />

                <div className="bg-purple-950/40 border border-purple-500/30 p-2.5 rounded-lg w-full text-center">
                  <span className="text-[8px] text-zinc-500 block uppercase font-bold">3. ENFORCED ACTION</span>
                  <strong className="text-white text-[11px]">WorkSuite Ticket creation / Dispatch email briefing</strong>
                </div>
              </div>
            </div>

            {/* Provider and security notes */}
            <div className="grid grid-cols-2 gap-4 text-[10px] text-zinc-500 font-sans border-t border-zinc-900 pt-3">
              <p>🔐 No API credentials or credentials context are hardcoded inside the workflow schematic blocks.</p>
              <p>📍 Client must bind their personal provider subscription keys (Make, OpenAI, Gmail APIs) inside the local env settings.</p>
            </div>

            {/* Controls */}
            <div className="flex justify-between gap-4 pt-2 border-t border-zinc-900">
              <button
                onClick={() => setActiveWorkflowProduct(null)}
                className="py-2 px-5 bg-zinc-950 border border-zinc-850 text-zinc-400 rounded-lg hover:text-white"
              >
                Deselect
              </button>

              <button
                onClick={() => {
                  handleRequestSetup(activeWorkflowProduct);
                  setActiveWorkflowProduct(null);
                }}
                className="py-2 px-6 bg-cyan-500 hover:brightness-110 text-black font-black uppercase rounded-lg"
              >
                Import Blueprint &amp; Invoice setup
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* E. CODE PACK PREVIEW POPUP */}
      {activeCodeProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md overflow-y-auto font-mono text-xs text-slate-300">
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="bg-[#030308] border border-cyan-500/40 rounded-2xl p-6 max-w-2xl w-full relative space-y-6"
          >
            <button
              onClick={() => setActiveCodeProduct(null)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <div>
              <span className="text-cyan-400 bg-cyan-900/10 border border-cyan-500/35 px-2 py-0.5 rounded font-bold uppercase text-[9px]">
                📂 SECURE CODE CODENAME REGISTRY BLOCK
              </span>
              <h2 className="text-lg font-sans font-black text-white uppercase mt-2">{activeCodeProduct.productName}</h2>
            </div>

            {/* Files & Dependency Specs */}
            <div className="grid grid-cols-2 gap-4 text-[10px] text-zinc-400 bg-[#060714] p-4 rounded-lg border border-zinc-900">
              <div>
                <strong className="text-cyan-400 block mb-1">BLUEPRINT ROOT DIRECTORY:</strong>
                {activeCodeProduct.includedFiles.map(file => (
                  <p key={file}>📄 {file}</p>
                ))}
              </div>
              <div>
                <strong className="text-[#e840f3] block mb-1">DEPENDENCY PACKAGES:</strong>
                <p>⚙️ framework: {activeCodeProduct.techStack.join(', ')}</p>
                <p>🔌 targets: {activeCodeProduct.integrations.join(', ') || 'N/A'}</p>
                <p>🛡️ licence: {activeCodeProduct.licenceType}</p>
              </div>
            </div>

            {/* Retro terminal content view */}
            <div className="space-y-1.5">
              <span className="text-[10px] text-purple-400 uppercase font-bold">Terminal Source Code Preview:</span>
              <div className="bg-black/95 text-[#00ff00] p-4.5 rounded-lg border border-purple-950/40 h-48 overflow-y-auto text-[10.5px] leading-relaxed font-mono">
                <p className="text-zinc-500"># NexoraX Automated Export Package Schema v21.08</p>
                <p className="text-zinc-500"># Project: {activeCodeProduct.productName}</p>
                <p className="text-zinc-500"># Authorized use only under B2B license guidelines.</p>
                <p className="pt-2">import React from 'react';</p>
                <p>import &#123; GoogleGenAI &#125; from "@google/genai";</p>
                <p className="pt-2">export const NexoraXAutomationCore = () =&gt; &#123;</p>
                <p className="pl-4">const apiKey = process.env.API_KEY_SECRET; </p>
                <p className="pl-4">const ai = new GoogleGenAI(&#123; apiKey &#125;);</p>
                <p className="pl-4 text-zinc-500">// Ensure human review before external outputs </p>
                <p className="pl-4">return (<span className="text-amber-300">...completed visual cards nodes...</span>);</p>
                <p>&#125;;</p>
              </div>
            </div>

            {/* Controls */}
            <div className="flex justify-between gap-4 pt-2 border-t border-zinc-900">
              <button
                onClick={() => setActiveCodeProduct(null)}
                className="py-2 px-5 bg-zinc-950 border border-zinc-850 text-zinc-400 rounded-lg hover:text-white"
              >
                Exit terminal
              </button>

              <button
                onClick={() => {
                  handleRequestSetup(activeCodeProduct);
                  setActiveCodeProduct(null);
                }}
                className="py-2 px-6 bg-cyan-500 hover:brightness-110 text-black font-black uppercase rounded-lg"
              >
                Add Code Package to Sandbox
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ======================================================== */}
      {/* UNIFIED CHECKOUT & REQUEST WIZARD MODAL (Tasks 1 to 5)  */}
      {/* ======================================================== */}
      {activeRequestProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-lg overflow-y-auto font-mono text-xs text-slate-300">
          <motion.div
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#020205] border border-purple-500/40 rounded-2xl p-6 max-w-2xl w-full relative space-y-6 shadow-2xl"
          >
            <button
              onClick={() => setActiveRequestProduct(null)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Title Header */}
            <div>
              <span className="text-purple-400 bg-purple-950/40 border border-purple-500/35 px-2.5 py-0.5 rounded uppercase font-black tracking-widest text-[8.5px] inline-block">
                SECURE CHECKOUT &amp; DELIVERY INTEGRATOR
              </span>
              <h2 className="text-base font-sans font-black text-white uppercase mt-2">
                CONFIRM SETTLEMENT: {activeRequestProduct.productName}
              </h2>
            </div>

            {/* Progress metrics */}
            <div className="grid grid-cols-5 gap-1 text-[8.5px] uppercase font-bold tracking-wider text-center border-b border-zinc-900 pb-3">
              {[1, 2, 3, 4, 5].map((step) => (
                <div key={step} className="space-y-1">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center mx-auto text-[9px] font-black ${requestStep === step ? 'bg-purple-500 text-white shadow-[0_0_10px_rgba(232,64,243,0.4)]' : requestStep > step ? 'bg-emerald-600 text-white' : 'bg-zinc-900 text-zinc-600'}`}>
                    {step}
                  </div>
                  <span className={requestStep === step ? 'text-[#e840f3]' : 'text-zinc-500'}>
                    {step === 1 ? 'Product' : step === 2 ? 'Launch Tier' : step === 3 ? 'Discount' : step === 4 ? 'Extras' : 'Handshake'}
                  </span>
                </div>
              ))}
            </div>

            {/* STEP 1: CHOOSE PRODUCT PROFILE */}
            {requestStep === 1 && (
              <div className="space-y-4">
                <p className="text-zinc-400 font-sans leading-normal normal-case">
                  You have requested deployment for a high-performance digital asset blueprint. Confirm the specifications of the chosen structure below:
                </p>
                
                <div className="bg-[#050611] border border-zinc-900 p-4 rounded-xl flex gap-4 items-center">
                  <img src={activeRequestProduct.previewImage} alt="" className="w-16 h-16 object-cover rounded border border-zinc-800 shrink-0" />
                  <div className="space-y-1">
                    <span className="text-[8.5px] text-zinc-500 bg-black border border-zinc-850 px-2 py-0.5 rounded">{activeRequestProduct.productType}</span>
                    <h4 className="text-xs font-black text-white uppercase">{activeRequestProduct.productName}</h4>
                    <p className="text-[10px] text-zinc-400 italic line-clamp-1">{activeRequestProduct.shortDescription}</p>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => setRequestStep(2)}
                    className="py-2 px-6 bg-purple-600 text-white border border-purple-500 font-black rounded-lg cursor-pointer uppercase hover:bg-purple-500 transition-all flex items-center gap-1.5"
                  >
                    <span>Choose Delivery Option</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: CHOOSE DELIVERY / LAUNCH DESIGNATION */}
            {requestStep === 2 && (
              <div className="space-y-4">
                <p className="text-zinc-400 font-sans leading-normal">Select your preferred implementation tier:</p>
                
                <div className="grid grid-cols-1 gap-3">
                  {/* Template only */}
                  <button
                    type="button"
                    onClick={() => setRequestConfig({ ...requestConfig, deliveryOption: 'template' })}
                    className={`p-3.5 border text-left rounded-xl transition-all cursor-pointer flex justify-between items-center ${requestConfig.deliveryOption === 'template' ? 'bg-[#e840f3]/5 border-purple-500' : 'bg-black/30 border-zinc-900 hover:border-zinc-800'}`}
                  >
                    <div className="space-y-1">
                      <span className="text-[9px] uppercase font-black text-amber-500">Tier A: Template Repository Download Only</span>
                      <p className="text-[10px] text-zinc-400 normal-case font-sans">Receive complete source package codes, schemas, and asset trees only. Setup is fully self-managed.</p>
                    </div>
                    <div className="text-right shrink-0">
                      <strong className="text-amber-500 text-sm block">£{activeRequestProduct.productPrice}</strong>
                      <span className="text-[8.5px] text-zinc-500 uppercase">One-Off</span>
                    </div>
                  </button>

                  {/* Done-For-You setup */}
                  <button
                    type="button"
                    onClick={() => setRequestConfig({ ...requestConfig, deliveryOption: 'dfy' })}
                    className={`p-3.5 border text-left rounded-xl transition-all cursor-pointer flex justify-between items-center ${requestConfig.deliveryOption === 'dfy' ? 'bg-[#e840f3]/5 border-purple-500' : 'bg-black/30 border-zinc-900 hover:border-zinc-800'}`}
                  >
                    <div className="space-y-1">
                      <span className="text-[9px] uppercase font-black text-[#e840f3]">Tier B: Done-For-You Setup Package</span>
                      <p className="text-[10px] text-zinc-400 normal-case font-sans">Senior developer sets up this blueprint file framework on your designated server with client credentials custom-bound.</p>
                    </div>
                    <div className="text-right shrink-0">
                      <strong className="text-white text-sm block">£{activeRequestProduct.setupPrice || 399}</strong>
                      <span className="text-[8.5px] text-zinc-500 uppercase">Setup Fee</span>
                    </div>
                  </button>

                  {/* NexoraX Managed Launch Bundle */}
                  <button
                    type="button"
                    onClick={() => setRequestConfig({ ...requestConfig, deliveryOption: 'Managed Launch Bundle' })}
                    className={`p-3.5 border text-left rounded-xl transition-all cursor-pointer flex justify-between items-center ${requestConfig.deliveryOption === 'Managed Launch Bundle' ? 'bg-[#e840f3]/10 border-[#e840f3] shadow-[0_0_15px_rgba(232,64,243,0.15)]' : 'bg-black/30 border-zinc-900 hover:border-[#e840f3]/25'}`}
                  >
                    <div className="space-y-1">
                      <span className="text-[9.5px] uppercase font-black text-cyan-400 flex items-center gap-1">
                        Tier C: NexoraX Managed Launch Bundle
                        <span className="bg-[#e5c158] text-black text-[7.5px] font-black px-1 rounded">SAVE 40%</span>
                      </span>
                      <p className="text-[10px] text-zinc-400 normal-case font-sans">Complete turnkey implementation including fully hosted setups, customized email registers, domain configuration &amp; active dashboard support.</p>
                    </div>
                    <div className="text-right shrink-0 pl-3">
                      <strong className="text-cyan-400 text-sm block leading-none mb-1">£{Math.floor((activeRequestProduct.setupPrice || 399) * 0.6)}</strong>
                      <span className="text-[8.5px] text-zinc-500 uppercase line-through block font-bold">£{activeRequestProduct.setupPrice}</span>
                    </div>
                  </button>
                </div>

                <div className="flex justify-between pt-2">
                  <button
                    onClick={() => setRequestStep(1)}
                    className="py-2 px-4 border border-zinc-800 text-zinc-400 rounded-lg cursor-pointer uppercase"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setRequestStep(3)}
                    className="py-2 px-6 bg-purple-600 text-white font-black rounded-lg cursor-pointer uppercase hover:bg-purple-500 transition-all flex items-center gap-1.5"
                  >
                    <span>Verify Discount Criteria</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: DISCOUNT AUDIT & EXPLANATIONS */}
            {requestStep === 3 && (
              <div className="space-y-4">
                <span className="text-[10px] text-purple-400 uppercase font-black tracking-widest block border-b border-zinc-900 pb-1.5">
                  ⚙️ DESIGNATED DISCOUNT AUDIT WINDOW
                </span>

                {requestConfig.deliveryOption === 'Managed Launch Bundle' ? (
                  <div className="space-y-4">
                    <div className="bg-emerald-950/15 border border-emerald-500/35 rounded-xl p-4 flex gap-3.5 items-start">
                      <CheckSquare className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <div className="space-y-1.5 normal-case font-sans text-xs">
                        <p className="text-[#00ff00] font-black uppercase font-mono text-[9px] tracking-wider">Managed Launch Discount Active and Applied: Save 40%</p>
                        <p>The 40% Managed Launch Discount applies to eligible digital products when setup, hosting, domain/email configuration, and ongoing support are managed through NexoraX. Standard setup fee £{activeRequestProduct.setupPrice} has been updated to £{Math.floor(activeRequestProduct.setupPrice * 0.6)} in settlement queue.</p>
                        <p className="text-zinc-500 italic text-[11px]">Note: Domain ownership remains fully with the client. No hidden lock-in penalties are applied. Hosting can be migrated gracefully at standard technical request.</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-950/15 border border-amber-900/30 rounded-xl p-4 flex gap-3.5 items-start">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div className="space-y-1.5 normal-case font-sans text-xs">
                      <p className="text-amber-500 font-black uppercase font-mono text-[9px] tracking-wider">Standard Catalog Price Triggered</p>
                      <p>You have selected standard template/setup delivery. The 40% Managed Launch Discount applies to eligible digital products when setup, hosting, domain/email configuration, and ongoing support are managed through NexoraX. If external hosting or self-managed setup is selected, standard template or setup pricing applies.</p>
                      <button
                        onClick={() => setRequestConfig({ ...requestConfig, deliveryOption: 'Managed Launch Bundle' })}
                        className="mt-2 py-1.5 px-3 bg-purple-900 border border-purple-500/35 uppercase tracking-widest text-[8.5px] font-black text-white rounded cursor-pointer block hover:brightness-110"
                      >
                        Activate Managed Launch Discount
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex justify-between pt-2">
                  <button
                    onClick={() => setRequestStep(2)}
                    className="py-2 px-4 border border-zinc-800 text-zinc-400 rounded-lg cursor-pointer uppercase"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setRequestStep(4)}
                    className="py-2 px-6 bg-purple-600 text-white font-black rounded-lg cursor-pointer uppercase hover:bg-purple-500 transition-all flex items-center gap-1.5"
                  >
                    <span>Choose Optional Extras</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: EXTRA ADDONS CONFIG */}
            {requestStep === 4 && (
              <div className="space-y-4">
                <p className="text-zinc-400">Enhance your deployment with optional strategic addons:</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className={`p-3 border rounded-xl flex items-center justify-between cursor-pointer transition-colors ${requestConfig.extras.hosting ? 'bg-purple-950/25 border-[#e840f3]/50 text-white' : 'bg-black/30 border-zinc-900 text-zinc-500 hover:text-white'}`}>
                    <div className="space-y-0.5 text-left font-sans">
                      <span className="text-[9.5px] font-mono uppercase font-black tracking-wider block text-[#e840f3]">Managed Cloud Hosting</span>
                      <span className="text-[9.5px]">Continuous server container maintenance.</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={requestConfig.extras.hosting}
                      onChange={(e) => setRequestConfig({ ...requestConfig, extras: { ...requestConfig.extras, hosting: e.target.checked } })}
                      className="accent-purple-500 shrink-0 cursor-pointer ml-3"
                    />
                  </label>

                  <label className={`p-3 border rounded-xl flex items-center justify-between cursor-pointer transition-colors ${requestConfig.extras.domain ? 'bg-purple-950/25 border-[#e840f3]/50 text-white' : 'bg-black/30 border-zinc-900 text-zinc-500 hover:text-white'}`}>
                    <div className="space-y-0.5 text-left font-sans">
                      <span className="text-[9.5px] font-mono uppercase font-black tracking-wider block text-[#e840f3]">Domains &amp; SSL Handover</span>
                      <span className="text-[9.5px]">Configure secure professional DNS registers.</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={requestConfig.extras.domain}
                      onChange={(e) => setRequestConfig({ ...requestConfig, extras: { ...requestConfig.extras, domain: e.target.checked } })}
                      className="accent-purple-500 shrink-0 cursor-pointer ml-3"
                    />
                  </label>

                  <label className={`p-3 border rounded-xl flex items-center justify-between cursor-pointer transition-colors ${requestConfig.extras.seo ? 'bg-purple-950/25 border-cyan-500/50 text-white' : 'bg-black/30 border-zinc-900 text-zinc-500 hover:text-white'}`}>
                    <div className="space-y-0.5 text-left font-sans">
                      <span className="text-[9.5px] font-mono uppercase font-black tracking-wider block text-cyan-400">Strategic SEO Setup</span>
                      <span className="text-[9.5px]">Local Google map listing validation index.</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={requestConfig.extras.seo}
                      onChange={(e) => setRequestConfig({ ...requestConfig, extras: { ...requestConfig.extras, seo: e.target.checked } })}
                      className="accent-cyan-400 shrink-0 cursor-pointer ml-3"
                    />
                  </label>

                  <label className={`p-3 border rounded-xl flex items-center justify-between cursor-pointer transition-colors ${requestConfig.extras.social ? 'bg-purple-950/25 border-cyan-500/50 text-white' : 'bg-black/30 border-zinc-900 text-zinc-500 hover:text-white'}`}>
                    <div className="space-y-0.5 text-left font-sans">
                      <span className="text-[9.5px] font-mono uppercase font-black tracking-wider block text-cyan-400">Social Pitch Deck</span>
                      <span className="text-[9.5px]">Includes pitch templates for board presentation.</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={requestConfig.extras.social}
                      onChange={(e) => setRequestConfig({ ...requestConfig, extras: { ...requestConfig.extras, social: e.target.checked } })}
                      className="accent-cyan-400 shrink-0 cursor-pointer ml-3"
                    />
                  </label>
                </div>

                <div className="flex justify-between pt-2">
                  <button
                    onClick={() => setRequestStep(3)}
                    className="py-2 px-4 border border-zinc-800 text-zinc-400 rounded-lg cursor-pointer uppercase"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setRequestStep(5)}
                    className="py-2 px-6 bg-purple-600 text-white font-black rounded-lg cursor-pointer uppercase hover:bg-purple-500 transition-all flex items-center gap-1.5"
                  >
                    <span>Proceed to Handshake</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 5: HANDSHAKE / SIGN-OFF */}
            {requestStep === 5 && (
              <div className="space-y-4">
                <p className="text-zinc-400 font-sans leading-normal">Enter your business identifier parameters to configure B2B deployment:</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 font-mono text-left">
                  <div className="space-y-1">
                    <label className="text-zinc-500 text-[9px] uppercase font-black tracking-widest block">Client Name:</label>
                    <input
                      type="text"
                      value={requestConfig.clientName}
                      onChange={(e) => setRequestConfig({ ...requestConfig, clientName: e.target.value })}
                      className="w-full bg-[#050611] border border-zinc-900 rounded p-2 text-white"
                      placeholder="e.g. John Doe"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-zinc-500 text-[9px] uppercase font-black tracking-widest block">Business / Agency Name:</label>
                    <input
                      type="text"
                      value={requestConfig.clientBusiness}
                      onChange={(e) => setRequestConfig({ ...requestConfig, clientBusiness: e.target.value })}
                      className="w-full bg-[#050611] border border-zinc-900 rounded p-2 text-white"
                      placeholder="e.g. Acme Ltd"
                    />
                  </div>
                  <div className="space-y-1 col-span-1 sm:col-span-2">
                    <label className="text-zinc-500 text-[9px] uppercase font-black tracking-widest block">B2B email address:</label>
                    <input
                      type="email"
                      value={requestConfig.clientEmail}
                      onChange={(e) => setRequestConfig({ ...requestConfig, clientEmail: e.target.value })}
                      className="w-full bg-[#050611] border border-zinc-900 rounded p-2 text-white"
                      placeholder="johndoe@acme.com"
                    />
                  </div>
                  <div className="space-y-1 col-span-1 sm:col-span-2">
                    <label className="text-zinc-500 text-[9px] uppercase font-black tracking-widest block">Scribe custom requirements notes:</label>
                    <textarea
                      value={requestConfig.message}
                      onChange={(e) => setRequestConfig({ ...requestConfig, message: e.target.value })}
                      className="w-full bg-[#050611] border border-zinc-900 rounded p-2 text-white min-h-[50px] font-sans"
                      placeholder="Enter domain particulars or requested integrations notes..."
                    />
                  </div>
                </div>

                {/* Summary math table */}
                <div className="bg-[#040409] border border-zinc-900 p-4 rounded-xl text-[10px] space-y-2">
                  <span className="text-purple-400 uppercase font-black tracking-wider text-[9px] block">SETTLEMENT SUMMARY:</span>
                  <div className="flex justify-between text-zinc-500">
                    <span>Baseline Digital template:</span>
                    <span className="text-slate-300 font-bold">£{activeRequestProduct.productPrice}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500">
                    <span>Setup implementation tier ({requestConfig.deliveryOption}):</span>
                    <span className="text-white font-bold">
                      {requestConfig.deliveryOption === 'template' ? '£0' : requestConfig.deliveryOption === 'dfy' ? `£${activeRequestProduct.setupPrice}` : `£${Math.floor(activeRequestProduct.setupPrice * 0.6)}`}
                    </span>
                  </div>
                  {requestConfig.extras.seo && (
                    <div className="flex justify-between text-zinc-500">
                      <span>strategic local SEO optimization:</span>
                      <span className="text-cyan-400">£149 Setup</span>
                    </div>
                  )}
                  <hr className="border-zinc-900" />
                  <div className="flex justify-between text-xs font-black">
                    <span className="text-[#e5c158] uppercase">Est. Total setup settlement:</span>
                    <span className="text-[#e840f3]">
                      £{activeRequestProduct.productPrice + (requestConfig.deliveryOption === 'template' ? 0 : requestConfig.deliveryOption === 'dfy' ? activeRequestProduct.setupPrice : Math.floor(activeRequestProduct.setupPrice * 0.6)) + (requestConfig.extras.seo ? 149 : 0)}
                    </span>
                  </div>
                </div>

                {/* Safe Labels Notice */}
                <div className="bg-[#0a0a0f] border border-zinc-900/60 p-3 rounded text-[9px] text-zinc-500 text-left space-y-1 normal-case leading-normal font-sans">
                  <p>📝 <strong className="font-mono text-[8px] uppercase">Secure checkout verification required:</strong> No instant card billing is initialized. An administrator will review your requested parameters and issue an invoice draft corresponding to your accounting schedule. Handshake triggers direct Staff Coordinator task allocation.</p>
                </div>

                {/* Action buttons */}
                <div className="flex justify-between pt-2">
                  <button
                    onClick={() => setRequestStep(4)}
                    className="py-2 px-4 border border-zinc-800 text-zinc-400 rounded-lg cursor-pointer uppercase"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!requestConfig.clientName || !requestConfig.clientEmail) {
                        alert("Client Name and Client Email are mandatory parameters inside handshake phase.");
                        return;
                      }

                      // Execute local state dispatch (Settle the request successfully)
                      const customOptionsText = `Option: ${requestConfig.deliveryOption} | SEO: ${requestConfig.extras.seo ? "Yes" : "No"} | Social: ${requestConfig.extras.social ? "Yes" : "No"} | Client notes: ${requestConfig.message}`;
                      handleRequestSetup(activeRequestProduct, customOptionsText);

                      // Also append this Inquiry record into public requests for admin review
                      const newInq = {
                        id: `inq-${Date.now()}`,
                        name: requestConfig.clientName,
                        businessName: requestConfig.clientBusiness || 'Acme Group',
                        email: requestConfig.clientEmail,
                        website: '',
                        productTypeNeeded: activeRequestProduct.productType,
                        industry: activeRequestProduct.category,
                        budgetRange: `£${Math.floor(activeRequestProduct.setupPrice * 0.6)}`,
                        needHosting: requestConfig.extras.hosting,
                        needDomainEmail: requestConfig.extras.domain,
                        needAutomationWorkflow: requestConfig.extras.social,
                        needSlideDeck: requestConfig.extras.social,
                        message: requestConfig.message || 'turnkey setup deployment requested',
                        createdAt: new Date().toISOString()
                      };

                      setPublicInquiries(prev => [newInq, ...prev]);
                      setActiveRequestProduct(null);
                    }}
                    className="py-2.5 px-8 bg-gradient-to-r from-emerald-600 to-teal-500 font-mono text-[9.5px] font-black text-[#020202] uppercase tracking-widest rounded-xl hover:brightness-110 cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Check className="w-4 h-4" />
                    <span>Submit setup request</span>
                  </button>
                </div>
              </div>
            )}

          </motion.div>
        </div>
      )}

      {/* ======================================================== */}
      {/* PUBLIC FORM: REQUEST A CUSTOM DIGITAL PRODUCT (Task 12)   */}
      {/* ======================================================== */}
      <div className="bg-gradient-to-br from-[#060714] via-[#030308] to-purple-950/20 border border-purple-500/20 rounded-2xl p-6 md:p-8 space-y-6 relative shadow-2xl">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#e840f3] to-cyan-400 rounded-t-2xl" />
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <span className="text-[9px] bg-purple-950/40 text-purple-400 font-mono font-black uppercase tracking-widest border border-purple-800/40 px-2.5 py-0.5 rounded-full inline-block mb-1.5">
              📬 SUBMIT DIRECT STRATEGIC DEMANDS
            </span>
            <h3 className="text-lg md:text-xl font-sans font-black uppercase text-white tracking-tight">
              Request a Custom Digital Product Setup
            </h3>
            <p className="text-xs text-zinc-400 leading-normal normal-case mt-1 max-w-2xl font-medium">
              Do you require a strategic, bespoke ready-made application, custom automated workflow channels, or optimized sales pitch slide decks? Scribe your design parameters below.
            </p>
          </div>
          
          <div className="bg-cyan-950/10 border border-cyan-500/25 p-3 rounded-xl max-w-xs text-left shrink-0">
            <span className="text-[#00ffff] font-mono text-[10px] font-black uppercase tracking-wider block">🤝 EXPLICIT B2B DEPLOY GUARANTEE</span>
            <p className="text-[9.5px] text-zinc-400 normal-case leading-normal font-sans mt-1">Our Senior systems architect assesses all custom templates requests within 24 hours. Handshakes route directly to staff sprint queues.</p>
          </div>
        </div>

        <form 
          onSubmit={(e) => {
            e.preventDefault();
            const target = e.target as any;
            const name = target.elements.reqName.value;
            const business = target.elements.reqBusiness.value;
            const email = target.elements.reqEmail.value;
            const productType = target.elements.reqType.value;
            const industry = target.elements.reqIndustry.value;
            const budgetRange = target.elements.reqBudget.value;
            const message = target.elements.reqMessage.value;
            const consentContact = target.elements.reqConsent.checked;
            
            if (!name || !email || !consentContact) {
              alert("Name, Email, and Contact Consent parameters are mandatory to initialize inquiry matrix.");
              return;
            }

            const newInq = {
              id: `inq-${Date.now()}`,
              name: name,
              businessName: business || 'Acme Group',
              email: email,
              website: target.elements.reqWebsite.value || '',
              productTypeNeeded: productType,
              industry: industry,
              budgetRange: budgetRange,
              needHosting: target.elements.reqHosting.checked,
              needDomainEmail: target.elements.reqDomain.checked,
              needAutomationWorkflow: target.elements.reqWorkflow.checked,
              needSlideDeck: target.elements.reqSlideDeck.checked,
              message: message,
              createdAt: new Date().toISOString()
            };

            setPublicInquiries(prev => [newInq, ...prev]);
            onAddLog('Public Custom Inquiry', `Dispatched new custom project scope draft for "${newInq.businessName}"`, 'SUCCESS', `Requested category: ${newInq.productTypeNeeded}`);
            
            if (onAddTaskToWorkSuite) {
              onAddTaskToWorkSuite(`EVALUATE: Custom ${productType} for ${business || name}`, 'MEDIUM');
            }

            alert(
              `CUSTOM BLUEPRINT DISPATCHED!\n\n` +
              `Thank you ${name}. Your custom request for a "${productType}" is successfully registered in our director inbox.\n\n` +
              `Calculated Setup Estimate: B2B Quote preparing under direct review.`
            );

            // Reset form fields
            target.reset();
          }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 font-mono text-xs text-left"
        >
          <div className="space-y-1.5">
            <label className="text-purple-400 uppercase font-black text-[9px] tracking-wider block">Full Name:*</label>
            <input
              name="reqName"
              type="text"
              required
              className="w-full bg-black/60 border border-zinc-900 rounded-lg p-2.5 text-white placeholder-zinc-600 focus:border-purple-500"
              placeholder="e.g. Eleanor Vance"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-purple-400 uppercase font-black text-[9px] tracking-wider block">Business Name:</label>
            <input
              name="reqBusiness"
              type="text"
              className="w-full bg-black/60 border border-zinc-900 rounded-lg p-2.5 text-white placeholder-zinc-600 focus:border-purple-500"
              placeholder="e.g. Acme Corp"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-purple-400 uppercase font-black text-[9px] tracking-wider block">Email Address:*</label>
            <input
              name="reqEmail"
              type="email"
              required
              className="w-full bg-black/60 border border-zinc-900 rounded-lg p-2.5 text-white placeholder-zinc-600 focus:border-purple-500"
              placeholder="eleanor@nexorax.co.uk"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-purple-400 uppercase font-black text-[9px] tracking-wider block">Product Type needed:</label>
            <select name="reqType" className="w-full bg-black border border-zinc-900 rounded-lg p-2.5 text-white cursor-pointer focus:border-purple-500">
              <option value="Ready-Made Website">Ready-Made Website</option>
              <option value="Ready-Made Mobile App">Ready-Made Mobile App</option>
              <option value="Bespoke B2B Dashboard">Bespoke B2B Dashboard</option>
              <option value="AI Assistant Integration">AI Assistant Flow</option>
              <option value="Strategic Automation Workflow">Strategic Automation (Make/n8n)</option>
              <option value="Custom Source Code Pack">Custom Source Code Pack</option>
              <option value="Strategic Pitch Slide Deck">Strategic Pitch Slide Deck</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-purple-400 uppercase font-black text-[9px] tracking-wider block">Target Industry Sector:</label>
            <input
              name="reqIndustry"
              type="text"
              className="w-full bg-black/60 border border-zinc-900 rounded-lg p-2.5 text-white placeholder-zinc-600 focus:border-purple-500"
              placeholder="e.g. Real Estate, Healthcare"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-purple-400 uppercase font-black text-[9px] tracking-wider block">Target Budget Range:</label>
            <select name="reqBudget" className="w-full bg-black border border-zinc-900 rounded-lg p-2.5 text-white cursor-pointer focus:border-purple-500">
              <option value="Under £250">Under £250 (Basic template)</option>
              <option value="£250 - £1,000">£250 - £1,000 (Standard DFY Setup)</option>
              <option value="Over £1,000">Over £1,000 (Turnkey custom enterprise)</option>
            </select>
          </div>

          <div className="space-y-1.5 md:col-span-3">
            <label className="text-purple-400 uppercase font-black text-[9px] tracking-wider block">Current Website (optional Redirect target):</label>
            <input
              name="reqWebsite"
              type="text"
              className="w-full bg-black/60 border border-zinc-900 rounded-lg p-2.5 text-white placeholder-zinc-600 focus:border-purple-500"
              placeholder="https://example.com"
            />
          </div>

          <div className="space-y-1.5 md:col-span-3">
            <label className="text-purple-400 uppercase font-black text-[9px] tracking-wider block">Bespoke requirements description notes:*</label>
            <textarea
              name="reqMessage"
              required
              className="w-full bg-black/60 border border-zinc-900 rounded-lg p-2.5 text-white placeholder-zinc-600 min-h-[80px] focus:border-purple-500 font-sans"
              placeholder="Describe your design parameters, requested third-party API configurations, database schemas, or workflow rules deeply..."
            />
          </div>

          {/* Strategic checklist selectors */}
          <div className="md:col-span-3 grid grid-cols-2 sm:grid-cols-5 gap-3.5 pt-2 border-t border-zinc-950 text-[10px] text-zinc-500 font-bold">
            <label className="flex items-center gap-2 cursor-pointer hover:text-white">
              <input name="reqHosting" type="checkbox" defaultChecked className="accent-purple-500" />
              <span>Hosting Setup</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer hover:text-white">
              <input name="reqDomain" type="checkbox" defaultChecked className="accent-purple-500" />
              <span>Domain &amp; SSL</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer hover:text-white">
              <input name="reqWorkflow" type="checkbox" className="accent-purple-300" />
              <span>Automation logic</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer hover:text-white">
              <input name="reqSlideDeck" type="checkbox" className="accent-purple-300" />
              <span>Pitch Slide Deck</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer hover:text-white">
              <input type="checkbox" className="accent-purple-300" />
              <span>Bespoke dashboard</span>
            </label>
          </div>

          {/* Direct Consents compliance checklist */}
          <div className="md:col-span-3 space-y-2 border-t border-zinc-950 pt-3">
            
            <label className="flex items-start gap-2.5 cursor-pointer text-[10px] text-zinc-400 leading-normal normal-case font-sans">
              <input
                name="reqConsent"
                type="checkbox"
                required
                className="accent-purple-500 mt-0.5"
              />
              <span>
                <strong className="text-purple-300 font-mono text-[9px] uppercase font-black">Handshake Consent:*</strong> I agree to be contacted via secure direct frequencies (support@nexoraxdigital.co.uk, freephone or email) to receive customized quotes, technical blueprint draft calculations, or administrative briefings from Director Lead Director.
              </span>
            </label>

            {/* Marketing Checkbox (Optional, not pre-ticked) */}
            <label className="flex items-start gap-2.5 cursor-pointer text-[10.5px] text-zinc-500 leading-normal normal-case font-sans">
              <input
                type="checkbox"
                className="accent-purple-500 mt-0.5"
              />
              <span>
                (Optional) Receive strategic notification logs, latest release catalogs alerts, and competitor transformation intercept updates. I can rescind metadata registration tags gracefully at any direct request.
              </span>
            </label>

          </div>

          <div className="col-span-1 md:col-span-3 flex justify-end pt-2">
            <button
              type="submit"
              className="py-3 px-8 bg-gradient-to-r from-purple-800 to-[#e840f3] text-[#fbfbfb] tracking-wider text-[10px] font-black uppercase rounded-xl border border-purple-500/30 hover:brightness-110 active:scale-[0.98] transition-all cursor-pointer flex items-center justify-center gap-1.5"
            >
              <Plus size={14} />
              <span>Dispatch Setup Specifications</span>
            </button>
          </div>
        </form>
      </div>

      {/* 8. CLIENT DASHBOARD DELIVERY MONITOR MODULE (Task 11) */}
      <div className="bg-black/45 border border-zinc-900 rounded-2xl p-6 space-y-6">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-emerald-500/10 text-[#00ff00] py-0.5 px-2.5 rounded-full border border-emerald-500/35 text-[9px] font-mono uppercase tracking-widest font-black mb-1.5">
            <CheckCircle className="w-3 h-3 text-[#00ff00]" />
            LIVE DELIVERY TRACKER
          </div>
          <h3 className="text-base font-sans font-black uppercase text-white tracking-tight">
            Client B2B Setup Delivery &amp; Sprints
          </h3>
          <p className="text-xs text-zinc-500">
            Track active server setups, SSL handovers, Docker compilation sprints, and project file transfers.
          </p>
        </div>

        {clientDeliveries.length === 0 ? (
          <div className="border border-dashed border-zinc-850 rounded-xl p-8 text-center text-zinc-500 text-xs font-mono">
            <Inbox className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
            <span>Zero active setups registered inside client profile. Choose an asset above to initiate build sprints.</span>
          </div>
        ) : (
          <div className="space-y-6">
            {clientDeliveries.map((del) => (
              <div key={del.id} className="bg-[#050610] border border-zinc-850 rounded-xl p-5 font-mono space-y-4">
                
                {/* Header info */}
                <div className="flex flex-col sm:flex-row justify-between gap-3 items-start sm:items-center border-b border-zinc-900 pb-3">
                  <div className="flex gap-3 items-center">
                    <img src={del.previewImage} alt="" className="w-10 h-10 object-cover rounded border border-zinc-800" />
                    <div>
                      <h4 className="text-xs font-sans font-black text-white uppercase">{del.productName}</h4>
                      <p className="text-[10px] text-zinc-500 mt-0.5">Vector: {del.type} | ID: {del.workSuiteId}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-slate-500 uppercase">Status:</span>
                    <span className={`px-2.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                      del.status === 'Request Received' ? 'bg-[#e840f3]/10 text-[#e840f3] border border-[#e840f3]/30' :
                      del.status === 'In Progress' ? 'bg-[#00ffff]/10 text-[#00ffff] border border-[#00ffff]/30 animate-pulse' :
                      'bg-emerald-500/10 text-[#00ff00] border border-emerald-500/30'
                    }`}>
                      {del.status}
                    </span>
                  </div>
                </div>

                <p className="text-[10px] text-[#00ffff]">⚙️ active configuration: <span className="text-zinc-400 capitalize-none">{del.config}</span></p>

                {/* Sprints status list */}
                <div className="space-y-2">
                  <span className="text-[9.5px] uppercase font-bold text-zinc-500 tracking-wider block">Deployment Checklist:</span>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[10px]">
                    {del.checklist.map((item: any, idx: number) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          const updatedChecklist = [...del.checklist];
                          updatedChecklist[idx].done = !updatedChecklist[idx].done;
                          
                          // Auto update active status
                          let newStatus = del.status;
                          const completedCount = updatedChecklist.filter(c => c.done).length;
                          if (completedCount === updatedChecklist.length) {
                            newStatus = 'Delivered';
                          } else if (completedCount > 1) {
                            newStatus = 'In Progress';
                          } else {
                            newStatus = 'Request Received';
                          }

                          setClientDeliveries(prev => prev.map(d => d.id === del.id ? {
                            ...d,
                            checklist: updatedChecklist,
                            status: newStatus
                          } : d));
                          onAddLog('Client Delivery', `Asset checklist updated: [${idx + 1}/${updatedChecklist.length}] done.`, 'SUCCESS', `Project tracker: ${del.productName}`);
                        }}
                        className={`p-2 bg-black/60 border text-left rounded-lg transition-colors cursor-pointer flex items-center gap-2 ${item.done ? 'border-emerald-500/30 text-emerald-400' : 'border-zinc-900 text-zinc-500 hover:border-zinc-800'}`}
                      >
                        {item.done ? <Check className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-zinc-700 shrink-0" />}
                        <span className="leading-tight capitalize-none">{item.task}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Secure downloads container */}
                <div className="flex flex-wrap pt-2 justify-between items-center bg-black p-3.5 rounded-xl border border-zinc-900">
                  <div className="text-[10px]">
                    <span className="text-zinc-400 block font-bold">🔒 FILES PACKAGE DELIVERY DIRECTORY:</span>
                    <span className="text-zinc-600 block mt-0.5 leading-normal">{del.files.join(', ') || 'Template Starter Code Packs'}</span>
                  </div>

                  <button
                    onClick={() => {
                      if (del.status !== 'Delivered') {
                        alert(`ACCESS CONTROL HOLD\n\nCheckout or invoice approval pending. Code package downloads are held securely until accounting clearance.`);
                        onAddLog('Client Delivery', 'Download rejected: Billing clearance missing.', 'DENIED', `Blocked code acquisition for standard item: ${del.productName}`);
                      } else {
                        onAddLog('Client Delivery', `Extracted download: [${del.productName}] source archive.`, 'SUCCESS', 'Handover sequence succeeded.');
                        alert(`SUCCESS! Direct download archive initialized for project files: ${del.files[0]}.`);
                      }
                    }}
                    className={`py-2 px-4 rounded-xl font-black text-[9px] uppercase tracking-wide cursor-pointer flex items-center gap-1.5 transition-all text-xs border ${
                      del.status === 'Delivered' 
                        ? 'bg-emerald-600 text-black border-emerald-500' 
                        : 'bg-zinc-950 text-zinc-600 border-zinc-900'
                    }`}
                  >
                    <Download size={12} />
                    <span>{del.status === 'Delivered' ? 'Access files through the Client Dashboard' : 'Files delivered through Client Dashboard after approval'}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};
